import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../context/AuthContext";
import { useNotification } from "../context/NotificationContext";
import { useNavigate } from "react-router-dom";
import Header from "./Header";
import SEO from "./SEO";
import { useTheme } from "../context/ThemeContext";
import {
  Check,
  Loader,
  Users,
  TrendingUp,
  Calendar,
  ShieldCheck,
  ShieldOff,
  Trash2,
  UserX,
  UserCheck,
  MoreVertical,
  Mail,
  ChevronUp,
  ChevronDown,
  BarChart3,
  PieChart,
  ChevronRight,
  ChevronLeft,
  BookOpen,
  CheckCircle,
  XCircle,
  ExternalLink,
} from "lucide-react";
import ConfirmDialog from "./ConfirmDialog";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";
import "./Admin.css";
import Footer from "./Footer";
import {
  isInPaymentGracePeriod,
  shouldBlockSubscriptionAccess,
} from "../utils/subscriptionAccess";
import CustomDatePicker from "./common/CustomDatePicker";

// Mirrors VALID_PRACTICE_AREAS in backend/routes/directory.js
const ADMIN_PRACTICE_AREAS = [
  "Civil Litigation",
  "Business / Corporate / Contracts",
  "Labor & Employment",
  "Criminal Defense / Post-Conviction",
  "Personal Injury / Insurance",
  "Family Law",
  "Estate Planning / Probate",
  "Immigration",
  "Real Estate / Construction",
  "Bankruptcy / Debt Relief",
  "Other",
];

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

/** Viewport-safe position for the user row ⋮ menu (escapes table overflow clipping). */
function computeAdminActionsMenuLayout(triggerEl) {
  const rect = triggerEl.getBoundingClientRect();
  const menuWidth = 200;
  const margin = 8;
  let left = rect.right - menuWidth;
  left = Math.max(margin, Math.min(left, window.innerWidth - menuWidth - margin));
  const estHeight = 280;
  let top = rect.bottom + 4;
  if (top + estHeight > window.innerHeight - margin) {
    const topAbove = rect.top - estHeight - 4;
    if (topAbove >= margin) {
      top = topAbove;
    } else {
      top = margin;
    }
  }
  const maxHeight = Math.max(160, window.innerHeight - top - margin);
  return { top, left, width: menuWidth, maxHeight };
}

const toLocalDateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const columnHelper = createColumnHelper();

export default function Admin() {
  const {
    isAuthenticated,
    isAdmin,
    loading: authLoading,
    user: currentUser,
    verifyToken,
  } = useAuth();
  const { showSuccess, showError, showWarning } = useNotification();
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingUsers, setUpdatingUsers] = useState(new Set());
  const [deletingUsers, setDeletingUsers] = useState(new Set());
  const [deactivatingUsers, setDeactivatingUsers] = useState(new Set());
  const [refreshingAuth, setRefreshingAuth] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    user: null,
  });
  const [adminStatusDialog, setAdminStatusDialog] = useState({
    isOpen: false,
    user: null,
    newStatus: null,
  });
  const [userStatusDialog, setUserStatusDialog] = useState({
    isOpen: false,
    user: null,
    newStatus: null,
  });
  const [openMenuId, setOpenMenuId] = useState(null);
  const [actionsMenuLayout, setActionsMenuLayout] = useState(null);
  const [addTokensDialog, setAddTokensDialog] = useState({
    isOpen: false,
    user: null,
    tokensToAdd: "",
  });
  const [addingTokens, setAddingTokens] = useState(new Set());
  const [cancelingSubscriptions, setCancelingSubscriptions] = useState(new Set());
  const [inviteTesterDialog, setInviteTesterDialog] = useState({
    isOpen: false,
    email: "",
    initialAiActionLimit: 800,
  });
  const [inviteUserDialog, setInviteUserDialog] = useState({
    isOpen: false,
    email: "",
    initialAiActionLimit: 800,
  });
  const [sendingInvite, setSendingInvite] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [adminFilter, setAdminFilter] = useState("all"); // all | admin | user
  const [providerFilter, setProviderFilter] = useState("all"); // all | local | google | other
  const [sorting, setSorting] = useState([{ id: "createdAt", desc: true }]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [pageSizeInput, setPageSizeInput] = useState("10");
  const [adminMainTab, setAdminMainTab] = useState("users");
  const [tokenStart, setTokenStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return toLocalDateString(d);
  });
  const [tokenEnd, setTokenEnd] = useState(() =>
    toLocalDateString(new Date()),
  );
  const [tokenAnalytics, setTokenAnalytics] = useState(null);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenError, setTokenError] = useState(null);
  /** "7" | "30" | "90" | "custom" — matches date range pills */
  const [tokenRangePreset, setTokenRangePreset] = useState("30");
  const [usageCostData, setUsageCostData] = useState(null);
  const [usageCostLoading, setUsageCostLoading] = useState(false);
  const [usageCostError, setUsageCostError] = useState(null);
  const [usageStart, setUsageStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return toLocalDateString(d);
  });
  const [usageEnd, setUsageEnd] = useState(() =>
    toLocalDateString(new Date()),
  );
  const [usageRangePreset, setUsageRangePreset] = useState("30");
  const [usageSearch, setUsageSearch] = useState("");
  const [usageSort, setUsageSort] = useState({
    key: "cost",
    dir: "desc",
  });
  const [usagePage, setUsagePage] = useState(0);
  const usagePageSize = 5;
  const [usageExpandedId, setUsageExpandedId] = useState(null);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalAIActions: 0,
    avgAIActionsPerUser: 0,
    totalAIActions: 0,
    avgAIActionsPerUser: 0,
  });

  const closeActionsMenu = useCallback(() => {
    setOpenMenuId(null);
    setActionsMenuLayout(null);
  }, []);

  const getAuthHeaders = () => {
    const headers = {
      "Content-Type": "application/json",
    };
    const token = localStorage.getItem("token");
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    return headers;
  };

  // Directory moderation state
  const [dirListings, setDirListings] = useState([]);
  const [dirCounts, setDirCounts] = useState({ all: 0, pending: 0, approved: 0, rejected: 0 });
  const [dirStatusFilter, setDirStatusFilter] = useState("all");
  const [dirSearchTerm, setDirSearchTerm] = useState("");
  const [dirLoading, setDirLoading] = useState(false);
  const [dirPage, setDirPage] = useState(1);
  const [dirTotalPages, setDirTotalPages] = useState(1);
  const [dirTotal, setDirTotal] = useState(0);
  const [dirLimit, setDirLimit] = useState(15);
  const [dirLimitInput, setDirLimitInput] = useState("15");
  const [dirDeleteConfirm, setDirDeleteConfirm] = useState({ isOpen: false, id: null, name: "" });
  const [dirDeleting, setDirDeleting] = useState(false);
  const [dirEdit, setDirEdit] = useState(null);

  /**
   * Overlay props for a dialog that closes on an outside click.
   *
   * A click event fires on the nearest common ancestor of its mousedown and
   * its mouseup. Drag-select a phone number inside the dialog, release the
   * button past its edge, and that ancestor is the overlay — so a plain
   * onClick on the overlay closed the dialog mid-copy. Requiring the press to
   * have started on the overlay keeps outside-click closing while leaving any
   * gesture that began inside the dialog alone.
   */
  const overlayPressRef = useRef(false);
  const overlayCloseProps = (close) => ({
    onMouseDown: (e) => {
      overlayPressRef.current = e.target === e.currentTarget;
    },
    onClick: (e) => {
      if (overlayPressRef.current && e.target === e.currentTarget) close();
    },
  });
  const [dirEditSaving, setDirEditSaving] = useState(false);
  const [dirEditError, setDirEditError] = useState("");

  useEffect(() => {
    setDirLimitInput(String(dirLimit));
  }, [dirLimit]);

  const fetchDirectoryListings = useCallback(async () => {
    try {
      setDirLoading(true);
      const query = new URLSearchParams();
      if (dirStatusFilter !== "all") query.set("status", dirStatusFilter);
      if (dirSearchTerm.trim()) query.set("search", dirSearchTerm.trim());
      query.set("page", dirPage);
      query.set("limit", dirLimit);

      const res = await fetch(`${API_URL}/directory/admin/listings?${query.toString()}`, {
        headers: getAuthHeaders(),
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to fetch directory listings");

      setDirListings(data.listings || []);
      if (data.counts) setDirCounts(data.counts);
      if (data.pagination) {
        setDirTotalPages(data.pagination.totalPages || 1);
        setDirTotal(data.pagination.total || 0);
      }
    } catch (err) {
      console.error("Error fetching directory listings:", err);
      showError(err.message);
    } finally {
      setDirLoading(false);
    }
  }, [dirStatusFilter, dirSearchTerm, dirPage, dirLimit, showError]);

  useEffect(() => {
    if (adminMainTab === "directory" && isAdmin) {
      fetchDirectoryListings();
    }
  }, [adminMainTab, isAdmin, fetchDirectoryListings]);

  const handleUpdateDirStatus = async (id, status) => {
    try {
      const res = await fetch(`${API_URL}/directory/admin/listings/${id}/status`, {
        method: "PATCH",
        headers: getAuthHeaders(),
        credentials: "include",
        body: JSON.stringify({ status }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to update status");
      showSuccess(`Listing status updated to ${status}`);
      fetchDirectoryListings();
    } catch (err) {
      showError(err.message);
    }
  };

  const handleEditDirListingClick = (item) => {
    setDirEditError("");
    setDirEdit({
      id: item._id,
      firstName: item.firstName || "",
      lastName: item.lastName || "",
      firmName: item.firmName || "",
      yearsInPractice: item.yearsInPractice || "",
      city: item.city || "",
      state: item.state || "",
      role: item.role || "",
      website: item.website || "",
      // Read-only: the private address the submitter verified with. It is the
      // listing's identity (duplicate check, account provisioning), so admins
      // can see it to get in touch but not edit it here.
      verificationEmail: item.email || "",
      emailPublic: item.emailPublic || "",
      publishEmail: !!item.publishEmail,
      phone: item.phone || "",
      phonePublic: item.phonePublic || "",
      practiceAreas: item.practiceAreas || [],
      practiceAreaOther: item.practiceAreaOther || "",
      publicList: !!item.publicList,
      verified: !!item.verified,
      emailVerified: !!item.emailVerified,
      freeConsultation: !!item.freeConsultation,
    });
  };

  const handleDirEditField = (field, value) => {
    setDirEdit((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleDirEditPracticeArea = (area) => {
    setDirEdit((prev) => {
      if (!prev) return prev;
      const current = prev.practiceAreas || [];
      if (current.includes(area)) {
        return { ...prev, practiceAreas: current.filter((a) => a !== area) };
      }
      if (current.length >= 3) return prev;
      return { ...prev, practiceAreas: [...current, area] };
    });
  };

  const patchDirListing = async (id, updates) => {
    const res = await fetch(`${API_URL}/directory/admin/listings/${id}`, {
      method: "PATCH",
      headers: getAuthHeaders(),
      credentials: "include",
      body: JSON.stringify(updates),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Failed to update listing");
    return data;
  };

  const handleSaveDirListing = async () => {
    if (!dirEdit) return;
    const { id, verificationEmail, ...updates } = dirEdit;
    try {
      setDirEditSaving(true);
      setDirEditError("");
      await patchDirListing(id, updates);
      showSuccess("Listing updated successfully");
      setDirEdit(null);
      fetchDirectoryListings();
    } catch (err) {
      setDirEditError(err.message);
    } finally {
      setDirEditSaving(false);
    }
  };

  const handleToggleDirVerified = async (item) => {
    try {
      await patchDirListing(item._id, { verified: !item.verified });
      showSuccess(
        item.verified ? "Listing marked as unverified" : "Listing marked as verified",
      );
      fetchDirectoryListings();
    } catch (err) {
      showError(err.message);
    }
  };

  const handleDeleteDirListingClick = (item) => {
    setDirDeleteConfirm({
      isOpen: true,
      id: item._id,
      name: `${item.firstName || ""} ${item.lastName || ""}`.trim() || "this listing",
    });
  };

  const handleDeleteDirListingCancel = () => {
    setDirDeleteConfirm({ isOpen: false, id: null, name: "" });
  };

  const handleDeleteDirListingConfirm = async () => {
    const { id } = dirDeleteConfirm;
    if (!id) return;
    try {
      setDirDeleting(true);
      const res = await fetch(`${API_URL}/directory/admin/listings/${id}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to delete listing");
      showSuccess("Listing deleted successfully");
      fetchDirectoryListings();
      setDirDeleteConfirm({ isOpen: false, id: null, name: "" });
    } catch (err) {
      showError(err.message);
    } finally {
      setDirDeleting(false);
    }
  };

  const toLocalDayRangeIso = (startDate, endDate) => {
    const startLocal = new Date(`${startDate}T00:00:00.000`);
    const endLocal = new Date(`${endDate}T23:59:59.999`);
    return {
      startIso: startLocal.toISOString(),
      endIso: endLocal.toISOString(),
    };
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/auth/admin/users`, {
        headers: getAuthHeaders(),
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error("Access denied. Admin privileges required.");
        }
        throw new Error("Failed to fetch users");
      }

      const data = await response.json();
      const usersList = data.users || [];
      setUsers(usersList);

      const totalAIActions = usersList.reduce(
        (sum, user) => sum + (user.subscriptionStatus === "trialing" ? (user.freeTrialTokensUsed || 0) : (user.aiActionsUsed || 0)),
        0,
      );
      const totalUsers = usersList.length;
      setStats({
        totalUsers,
        totalAIActions,
        avgAIActionsPerUser:
          totalUsers > 0 ? Math.round(totalAIActions / totalUsers) : 0,
      });
    } catch (err) {
      console.error("Error fetching users:", err);
      setError(err.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const fetchTokenAnalytics = async (startOverride, endOverride) => {
    try {
      setTokenLoading(true);
      setTokenError(null);
      let s = startOverride ?? tokenStart;
      let e = endOverride ?? tokenEnd;

      if (!s) {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        s = toLocalDateString(d);
        setTokenStart(s);
      }
      if (!e) {
        e = toLocalDateString(new Date());
        setTokenEnd(e);
      }

      const { startIso, endIso } = toLocalDayRangeIso(s, e);
      const url = `${API_URL}/auth/admin/token-analytics?start=${encodeURIComponent(startIso)}&end=${encodeURIComponent(endIso)}`;
      const response = await fetch(url, {
        headers: getAuthHeaders(),
        credentials: "include",
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || "Failed to load token analytics");
      }
      const data = await response.json();
      setTokenAnalytics(data);
    } catch (err) {
      console.error(err);
      setTokenError(err.message || "Failed to load token analytics");
      setTokenAnalytics(null);
    } finally {
      setTokenLoading(false);
    }
  };

  useEffect(() => {
    if (adminMainTab !== "tokens" || !isAdmin) return;
    fetchTokenAnalytics();
    // tokenStart/tokenEnd applied via Apply range button
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminMainTab, isAdmin]);

  useEffect(() => {
    if (adminMainTab !== "tokens" || !isAdmin) return;
    if (tokenRangePreset !== "custom") return;
    if (!tokenStart || !tokenEnd) return;
    fetchTokenAnalytics(tokenStart, tokenEnd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminMainTab, isAdmin, tokenRangePreset, tokenStart, tokenEnd]);

  const fetchUsageCost = async (startOverride, endOverride) => {
    try {
      setUsageCostLoading(true);
      setUsageCostError(null);
      let s = startOverride ?? usageStart;
      let e = endOverride ?? usageEnd;

      if (!s) {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        s = toLocalDateString(d);
        setUsageStart(s);
      }
      if (!e) {
        e = toLocalDateString(new Date());
        setUsageEnd(e);
      }

      const { startIso, endIso } = toLocalDayRangeIso(s, e);
      const url = `${API_URL}/auth/admin/usage-cost?start=${encodeURIComponent(startIso)}&end=${encodeURIComponent(endIso)}`;
      const response = await fetch(url, {
        headers: getAuthHeaders(),
        credentials: "include",
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || "Failed to load usage & cost");
      }
      const data = await response.json();
      setUsageCostData(data);
    } catch (err) {
      console.error(err);
      setUsageCostError(err.message || "Failed to load usage & cost");
      setUsageCostData(null);
    } finally {
      setUsageCostLoading(false);
    }
  };

  useEffect(() => {
    if (adminMainTab !== "usage" || !isAdmin) return;
    fetchUsageCost();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminMainTab, isAdmin]);

  const applyUsagePresetDays = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    const s = toLocalDateString(start);
    const e = toLocalDateString(end);
    setUsageRangePreset(String(days));
    setUsageStart(s);
    setUsageEnd(e);
    fetchUsageCost(s, e);
  };

  const applyUsageThisMonth = () => {
    const end = new Date();
    const start = new Date(end.getFullYear(), end.getMonth(), 1);
    const s = toLocalDateString(start);
    const e = toLocalDateString(end);
    setUsageRangePreset("month");
    setUsageStart(s);
    setUsageEnd(e);
    fetchUsageCost(s, e);
  };

  const updateAdminStatus = async (userId, email, newAdminStatus) => {
    try {
      setUpdatingUsers((prev) => new Set(prev).add(userId));
      setError(null);

      const response = await fetch(
        `${API_URL}/auth/admin/users/${userId}/admin-status`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          credentials: "include",
          body: JSON.stringify({ isAdmin: newAdminStatus }),
        },
      );

      if (!response.ok) {
        // Check if response is JSON before parsing
        const contentType = response.headers.get("content-type");
        let errorMessage = "Failed to update admin status";

        if (contentType && contentType.includes("application/json")) {
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
          } catch (parseError) {
            console.error("Error parsing error response:", parseError);
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
          }
        } else {
          // Response is not JSON (likely HTML error page)
          const text = await response.text();
          console.error("Non-JSON error response:", text.substring(0, 200));
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }

        throw new Error(errorMessage);
      }

      const data = await response.json();

      // Update the user in the local state
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.userId === userId ? { ...user, is_Admin: newAdminStatus } : user,
        ),
      );

      // Show success message
      showSuccess(
        `Admin access ${newAdminStatus ? "granted" : "revoked"} for ${email}`,
      );
    } catch (err) {
      console.error("Error updating admin status:", err);
      setError(err.message || "Failed to update admin status");
      showError(`Error: ${err.message}`);
    } finally {
      setUpdatingUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  const handleDeleteClick = (user) => {
    setDeleteDialog({ isOpen: true, user });
  };

  const handleDeleteConfirm = async () => {
    const { user } = deleteDialog;
    if (!user) return;

    try {
      setDeletingUsers((prev) => new Set(prev).add(user.userId));
      setError(null);

      const response = await fetch(
        `${API_URL}/auth/admin/users/${user.userId}`,
        {
          method: "DELETE",
          headers: getAuthHeaders(),
          credentials: "include",
        },
      );

      if (!response.ok) {
        // Check if response is JSON before parsing
        const contentType = response.headers.get("content-type");
        let errorMessage = "Failed to delete user";

        if (contentType && contentType.includes("application/json")) {
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
          } catch (parseError) {
            console.error("Error parsing error response:", parseError);
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
          }
        } else {
          // Response is not JSON (likely HTML error page)
          const text = await response.text();
          console.error("Non-JSON error response:", text.substring(0, 200));
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }

        throw new Error(errorMessage);
      }

      // Remove user from local state
      setUsers((prevUsers) =>
        prevUsers.filter((u) => u.userId !== user.userId),
      );

      // Recalculate stats
      setStats((prevStats) => ({
        ...prevStats,
        totalUsers: prevStats.totalUsers - 1,
      }));

      // Close dialog
      setDeleteDialog({ isOpen: false, user: null });

      // Show success message
      showSuccess(`User ${user.email} has been deleted successfully`);
    } catch (err) {
      console.error("Error deleting user:", err);
      setError(err.message || "Failed to delete user");
      showError(`Error: ${err.message}`);
    } finally {
      setDeletingUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(user.userId);
        return newSet;
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ isOpen: false, user: null });
  };

  const handleAdminStatusConfirm = async () => {
    const { user, newStatus } = adminStatusDialog;
    if (!user) return;

    setAdminStatusDialog({ isOpen: false, user: null, newStatus: null });
    await updateAdminStatus(user.userId, user.email, newStatus);
  };

  const handleAdminStatusCancel = () => {
    setAdminStatusDialog({ isOpen: false, user: null, newStatus: null });
  };

  const handleUserStatusConfirm = async () => {
    const { user, newStatus } = userStatusDialog;
    if (!user) return;

    setUserStatusDialog({ isOpen: false, user: null, newStatus: null });
    await handleDeactivateUser(user.userId, user.email, !newStatus);
  };

  const handleUserStatusCancel = () => {
    setUserStatusDialog({ isOpen: false, user: null, newStatus: null });
  };

  const handleAddTokensClick = (user) => {
    setAddTokensDialog({ isOpen: true, user, tokensToAdd: "" });
  };

  const handleAddTokensCancel = () => {
    setAddTokensDialog({ isOpen: false, user: null, tokensToAdd: "" });
  };

  const handleInviteTesterClick = () => {
    setInviteTesterDialog({
      isOpen: true,
      email: "",
      initialAiActionLimit: 800,
    });
  };

  const handleInviteTesterCancel = () => {
    setInviteTesterDialog({
      isOpen: false,
      email: "",
      initialAiActionLimit: 800,
    });
  };

  const handleInviteUserClick = () => {
    setInviteUserDialog({
      isOpen: true,
      email: "",
      initialAiActionLimit: 800,
    });
  };

  const handleInviteUserCancel = () => {
    setInviteUserDialog({
      isOpen: false,
      email: "",
      initialAiActionLimit: 800,
    });
  };

  const handleInviteUserConfirm = async () => {
    const { email, initialAiActionLimit } = inviteUserDialog;
    if (!email || !email.trim()) {
      showError("Please enter a valid email address");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      showError("Please enter a valid email address");
      return;
    }

    // Enforce 1-800 limit
    if (initialAiActionLimit < 1 || initialAiActionLimit > 800) {
      showError("AI Action limit must be between 1 and 800");
      return;
    }

    try {
      setSendingInvite(true);
      setError(null);

      const response = await fetch(`${API_URL}/auth/admin/invite-user`, {
        method: "POST",
        headers: getAuthHeaders(),
        credentials: "include",
        body: JSON.stringify({ email: email.trim(), initialAiActionLimit }),
      });

      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        let errorMessage = "Failed to send user invitation";

        if (contentType && contentType.includes("application/json")) {
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
          } catch (parseError) {
            console.error("Error parsing error response:", parseError);
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
          }
        } else {
          const text = await response.text();
          console.error("Non-JSON error response:", text.substring(0, 200));
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }

        throw new Error(errorMessage);
      }

      // Close dialog
      setInviteUserDialog({
        isOpen: false,
        email: "",
        initialAiActionLimit: 800,
      });

      // Show success message
      showSuccess(`User invitation sent successfully to ${email.trim()}`);
    } catch (err) {
      console.error("Error sending user invitation:", err);
      setError(err.message || "Failed to send user invitation");
      showError(`Error: ${err.message}`);
    } finally {
      setSendingInvite(false);
    }
  };

  const handleInviteTesterConfirm = async () => {
    const { email, initialAiActionLimit } = inviteTesterDialog;
    if (!email || !email.trim()) {
      showError("Please enter a valid email address");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      showError("Please enter a valid email address");
      return;
    }

    // Enforce 1-800 limit
    if (initialAiActionLimit < 1 || initialAiActionLimit > 800) {
      showError("AI Action limit must be between 1 and 800");
      return;
    }

    try {
      setSendingInvite(true);
      setError(null);

      const response = await fetch(`${API_URL}/auth/admin/invite-tester`, {
        method: "POST",
        headers: getAuthHeaders(),
        credentials: "include",
        body: JSON.stringify({
          email: email.trim(),
          initialAiActionLimit,
        }),
      });

      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        let errorMessage = "Failed to send tester invitation";

        if (contentType && contentType.includes("application/json")) {
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
          } catch (parseError) {
            console.error("Error parsing error response:", parseError);
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
          }
        } else {
          const text = await response.text();
          console.error("Non-JSON error response:", text.substring(0, 200));
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }

        throw new Error(errorMessage);
      }

      // Close dialog
      setInviteTesterDialog({
        isOpen: false,
        email: "",
        initialAiActionLimit: 800,
      });

      // Show success message
      showSuccess(`Tester invitation sent successfully to ${email.trim()}`);
    } catch (err) {
      console.error("Error sending tester invitation:", err);
      setError(err.message || "Failed to send tester invitation");
      showError(`Error: ${err.message}`);
    } finally {
      setSendingInvite(false);
    }
  };

  const handleAddTokensConfirm = async () => {
    const { user, tokensToAdd } = addTokensDialog;
    if (!user || !tokensToAdd) return;

    const tokens = parseInt(tokensToAdd, 10);
    if (isNaN(tokens) || tokens <= 0) {
      showError("Please enter a valid positive number of tokens");
      return;
    }

    try {
      setAddingTokens((prev) => new Set(prev).add(user.userId));
      setError(null);

      const response = await fetch(
        `${API_URL}/auth/admin/users/${user.userId}/add-tokens`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          credentials: "include",
          body: JSON.stringify({ tokensToAdd: tokens }),
        },
      );

      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        let errorMessage = "Failed to add tokens";

        if (contentType && contentType.includes("application/json")) {
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
          } catch (parseError) {
            console.error("Error parsing error response:", parseError);
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
          }
        } else {
          const text = await response.text();
          console.error("Non-JSON error response:", text.substring(0, 200));
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }

        throw new Error(errorMessage);
      }

      const data = await response.json();

      // Update the user in the local state and recalculate stats
      setUsers((prevUsers) => {
        const updated = prevUsers.map((u) =>
          u.userId === user.userId
            ? {
              ...u,
              aiActionsUsed: data.user.aiActionsUsed,
              aiMonthlyLimit: data.user.aiMonthlyLimit,
            }
            : u,
        );
        const totalAIActions = updated.reduce(
          (sum, u) => sum + (u.aiActionsUsed || 0),
          0,
        );
        setStats((prevStats) => ({
          ...prevStats,
          totalAIActions,
        }));
        return updated;
      });

      // Close dialog
      setAddTokensDialog({ isOpen: false, user: null, tokensToAdd: "" });

      // Show success message
      showSuccess(
        `Successfully added ${tokens.toLocaleString()} AI actions to ${user.email}. New monthly limit: ${data.user.aiMonthlyLimit.toLocaleString()}`,
      );
    } catch (err) {
      console.error("Error adding tokens:", err);
      setError(err.message || "Failed to add tokens");
      showError(`Error: ${err.message}`);
    } finally {
      setAddingTokens((prev) => {
        const newSet = new Set(prev);
        newSet.delete(user.userId);
        return newSet;
      });
    }
  };

  const handleAdminCancelSubscription = async (user) => {
    try {
      setCancelingSubscriptions((prev) => new Set(prev).add(user.userId));
      const response = await fetch(
        `${API_URL}/auth/admin/users/${user.userId}/cancel-subscription`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          credentials: "include",
        },
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || "Failed to cancel subscription");
      }

      const result = await response.json();
      setUsers((prevUsers) =>
        prevUsers.map((u) =>
          u.userId === user.userId
            ? {
                ...u,
                cancelAtPeriodEnd: true,
              }
            : u,
        ),
      );
      showSuccess(
        `Subscription cancellation scheduled for ${user.email}${result.cancelAt ? ` (effective ${formatDate(result.cancelAt)})` : ""}.`,
      );
    } catch (err) {
      showError(err.message || "Failed to cancel subscription");
    } finally {
      setCancelingSubscriptions((prev) => {
        const updated = new Set(prev);
        updated.delete(user.userId);
        return updated;
      });
    }
  };

  const handleDeactivateUser = async (userId, email, currentStatus) => {
    try {
      setDeactivatingUsers((prev) => new Set(prev).add(userId));
      setError(null);

      const newStatus = !currentStatus; // Toggle status

      const response = await fetch(
        `${API_URL}/auth/admin/users/${userId}/status`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          credentials: "include",
          body: JSON.stringify({ isActive: newStatus }),
        },
      );

      if (!response.ok) {
        // Check if response is JSON before parsing
        const contentType = response.headers.get("content-type");
        let errorMessage = "Failed to update user status";

        if (contentType && contentType.includes("application/json")) {
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
          } catch (parseError) {
            console.error("Error parsing error response:", parseError);
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
          }
        } else {
          // Response is not JSON (likely HTML error page)
          const text = await response.text();
          console.error("Non-JSON error response:", text.substring(0, 200));
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }

        throw new Error(errorMessage);
      }

      const data = await response.json();

      // Update the user in the local state
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.userId === userId ? { ...user, isActive: newStatus } : user,
        ),
      );

      // Show success message
      showSuccess(
        `User ${newStatus ? "activated" : "deactivated"} successfully for ${email}`,
      );
    } catch (err) {
      console.error("Error updating user status:", err);
      setError(err.message || "Failed to update user status");
      showError(`Error: ${err.message}`);
    } finally {
      setDeactivatingUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    // If user is authenticated but not admin, try refreshing auth once
    if (!isAdmin && !refreshingAuth) {
      setRefreshingAuth(true);
      verifyToken().catch(console.error);
      return;
    }

    // After refresh, if still not admin, show error message (don't redirect immediately)
    // The component will show the error message UI
    if (!isAdmin) {
      return;
    }

    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, authLoading, navigate]);

  // Separate effect to handle admin check after refresh
  useEffect(() => {
    if (refreshingAuth && !authLoading) {
      setRefreshingAuth(false);
    }
  }, [authLoading, refreshingAuth]);

  // Close menu when clicking outside (menu renders in a portal, not inside .admin-actions-cell)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        openMenuId &&
        !event.target.closest(".admin-actions-cell") &&
        !event.target.closest(".admin-actions-menu-portal")
      ) {
        closeActionsMenu();
      }
    };

    if (openMenuId) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [openMenuId, closeActionsMenu]);

  useEffect(() => {
    if (!openMenuId) return undefined;
    const onScrollOrResize = () => closeActionsMenu();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [openMenuId, closeActionsMenu]);

  const actionsMenuUser = useMemo(
    () =>
      openMenuId
        ? users.find((u) => u.userId === openMenuId) ?? null
        : null,
    [users, openMenuId],
  );

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "Invalid Date";
    }
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "0";
    return num.toLocaleString("en-US");
  };

  const formatTokensShort = (n) => {
    if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
    const x = Number(n);
    if (x >= 1e6) return `${(x / 1e6).toFixed(1)}M`;
    if (x >= 1e3) return `${Math.round(x / 1e3)}K`;
    return String(Math.round(x));
  };

  const formatUsdCompact = (n, digits = 2) => {
    if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
    return `$${Number(n).toFixed(digits)}`;
  };

  const modelAnalyticsClass = (modelName) => {
    const m = String(modelName || "").toLowerCase();
    if (m.includes("claude")) return "ta-m-claude";
    if (m.includes("chatgpt") || m.includes("gpt")) return "ta-m-gpt";
    if (m.includes("gemini")) return "ta-m-gemini";
    if (m.includes("grok")) return "ta-m-grok";
    if (m.includes("perplexity") || m.includes("sonar")) return "ta-m-perp";
    return "ta-m-default";
  };

  // Keep these in sync with backend model selections.
  const CURRENT_MODELS_BY_PROVIDER = {
    Claude: "claude-sonnet-4-5",
    ChatGPT: "gpt-4o-mini",
    Gemini: "gemini-2.5-flash",
    Grok: "grok-3-mini",
    Perplexity: "sonar",
  };

  const getCurrentModelVersion = (providerName, reportedVersion) => {
    const normalizedProvider = String(providerName || "").trim();
    const normalizedReportedVersion = String(reportedVersion || "").trim();
    if (
      normalizedReportedVersion &&
      normalizedReportedVersion !== "(not reported)"
    ) {
      return normalizedReportedVersion;
    }
    return CURRENT_MODELS_BY_PROVIDER[normalizedProvider] || "";
  };

  const tierAnalyticsClass = (tier) => {
    if (tier === "trial") return "ta-t-trial";
    if (tier === "paid") return "ta-t-paid";
    return "ta-t-free";
  };

  const tierDisplayLabel = (tier) => {
    if (tier === "trial") return "Free trial";
    if (tier === "paid") return "Paid";
    return "Free tier";
  };

  const applyTokenPresetDays = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    const s = toLocalDateString(start);
    const e = toLocalDateString(end);
    setTokenRangePreset(String(days));
    setTokenStart(s);
    setTokenEnd(e);
    fetchTokenAnalytics(s, e);
  };

  const usageProcessedRows = useMemo(() => {
    if (!usageCostData?.users) return [];
    let list = [...usageCostData.users];
    const q = usageSearch.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (u) =>
          (u.name || "").toLowerCase().includes(q) ||
          (u.email || "").toLowerCase().includes(q),
      );
    }
    const dir = usageSort.dir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      switch (usageSort.key) {
        case "name":
          return dir * (a.name || "").localeCompare(b.name || "");
        case "plan":
          return dir * (a.planLabel || "").localeCompare(b.planLabel || "");
        case "actions":
          return dir * ((a.aiActionsUsed || 0) - (b.aiActionsUsed || 0));
        case "cost":
          return dir * ((a.costUsd || 0) - (b.costUsd || 0));
        case "joined": {
          const ta = a.joinedIso || "";
          const tb = b.joinedIso || "";
          return dir * ta.localeCompare(tb);
        }
        default:
          return 0;
      }
    });
    return list;
  }, [usageCostData, usageSearch, usageSort]);

  useEffect(() => {
    setUsagePage(0);
  }, [usageSearch, usageSort, usageCostData]);

  const usageMaxPage = Math.max(
    0,
    Math.ceil(usageProcessedRows.length / usagePageSize) - 1,
  );
  useEffect(() => {
    if (usagePage > usageMaxPage) setUsagePage(usageMaxPage);
  }, [usagePage, usageMaxPage]);

  const usageTotalPages = Math.max(
    1,
    Math.ceil(usageProcessedRows.length / usagePageSize),
  );
  const usagePageRows = usageProcessedRows.slice(
    usagePage * usagePageSize,
    (usagePage + 1) * usagePageSize,
  );

  const toggleUsageSort = (key) => {
    setUsageSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : {
          key,
          dir: key === "cost" || key === "actions" ? "desc" : "asc",
        },
    );
  };

  const getUserInitials = (name) => {
    if (!name || name === "—") return "?";
    const parts = String(name).trim().split(/\s+/);
    if (parts.length >= 2) {
      return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
    }
    return String(name).slice(0, 2).toUpperCase();
  };

  const getTotalTokensForUser = (user) => {
    if (user.totalTokensUsed !== undefined) {
      return user.totalTokensUsed;
    }
    // Fallback: calculate from tokensByModel
    const tokensByModel = user.tokensByModel || {};
    return Object.values(tokensByModel).reduce((sum, model) => {
      const tokens = typeof model === "number" ? model : model.totalTokens || 0;
      return sum + tokens;
    }, 0);
  };

  // Define columns using TanStack Table
  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: "Name",
        cell: (info) => {
          const user = info.row.original;
          const isActive = user.isActive !== false;
          return (
            <div
              className="admin-user-name"
              style={{
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {user.isAnonymous && (
                <span style={{ marginRight: "6px" }}>👤</span>
              )}
              {user.name || (user.isAnonymous ? "Guest User" : "N/A")}
              {user.isAnonymous && (
                <span
                  style={{
                    marginLeft: "6px",
                    fontSize: "0.75rem",
                    color: "#888",
                    fontStyle: "italic",
                  }}
                >
                  (Anonymous)
                </span>
              )}
              {!user.isAnonymous && !isActive && (
                <span
                  className="admin-deactivated-badge"
                  title="User is deactivated"
                  style={{ marginLeft: "6px" }}
                >
                  (Deactivated)
                </span>
              )}
            </div>
          );
        },
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          const a = (rowA.original.name || "").toLowerCase();
          const b = (rowB.original.name || "").toLowerCase();
          return a.localeCompare(b);
        },
      }),
      columnHelper.accessor("email", {
        header: "Email",
        cell: (info) => (
          <div className="admin-user-email" style={{ textAlign: "center" }}>
            {info.getValue()}
          </div>
        ),
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          const a = (rowA.original.email || "").toLowerCase();
          const b = (rowB.original.email || "").toLowerCase();
          return a.localeCompare(b);
        },
      }),
      columnHelper.accessor("role", {
        header: "Role",
        cell: (info) => {
          const user = info.row.original;
          // Display 'Admin' if user is an admin, otherwise use role from database or 'Other' as fallback
          const displayRole = user.isAnonymous
            ? "Guest"
            : user.is_Admin === true
              ? "Admin"
              : user.role || "Other";
          return (
            <div
              className="admin-status-cell"
              style={{
                textAlign: "center",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {displayRole}
            </div>
          );
        },
      }),
      columnHelper.accessor("subscriptionPlan", {
        header: "Subscription",
        cell: (info) => {
          const user = info.row.original;
          const subscriptionPlan = user.subscriptionPlan || "free";
          const subscriptionStatus = user.subscriptionStatus || "inactive";
          const isTrialing = subscriptionStatus === "trialing";
          const isPastDue = subscriptionStatus === "past_due";
          const isUnpaid = subscriptionStatus === "unpaid";
          const isDelinquent = isPastDue || isUnpaid;
          const paymentAlert = user.adminPaymentAlert;
          const inPaymentGrace = isInPaymentGracePeriod(user);
          const paymentAccessBlocked = shouldBlockSubscriptionAccess(user);
          const isPaid =
            subscriptionPlan === "essentials" ||
            subscriptionPlan === "professional";

          // Map plan names to display format (singular for Essential)
          const getPlanDisplayName = (plan) => {
            if (plan === "essentials") return "Essential";
            if (plan === "professional") return "Professional";
            return "Free";
          };

          const planDisplayName = getPlanDisplayName(subscriptionPlan);

          const delinquentBracketDetail = () => {
            if (paymentAccessBlocked) {
              return `${planDisplayName} — access suspended`;
            }
            if (inPaymentGrace) {
              return `${planDisplayName} — grace period`;
            }
            return `${planDisplayName} — update billing`;
          };

          // Plan + payment state in brackets (matches Pricing page messaging)
          let displayStatus;
          let paymentSubtitle = null;
          if (isUnpaid && isPaid) {
            displayStatus = `Unpaid (${planDisplayName} — access suspended)`;
            paymentSubtitle = "Payment failed — access suspended";
          } else if (isPastDue && isPaid) {
            displayStatus = `Past Due (${delinquentBracketDetail()})`;
            paymentSubtitle = paymentAccessBlocked
              ? "Payment failed — access suspended"
              : inPaymentGrace
                ? "Payment past due — grace period"
                : "Payment past due — update billing";
          } else if (isUnpaid) {
            displayStatus = `Unpaid (${planDisplayName} — access suspended)`;
            paymentSubtitle = "Payment failed — access suspended";
          } else if (isPastDue) {
            displayStatus = `Past Due (${delinquentBracketDetail()})`;
            paymentSubtitle = paymentAccessBlocked
              ? "Payment failed — access suspended"
              : "Payment past due — grace period";
          } else if (isTrialing && isPaid) {
            displayStatus = `Trial (${planDisplayName})`;
          } else if (isPaid) {
            displayStatus = planDisplayName;
          } else {
            displayStatus = "Free";
          }

          const showPaymentCritical = isUnpaid || (isPastDue && paymentAccessBlocked);
          const showPaymentWarning = isPastDue && !paymentAccessBlocked;

          const graceEndsLabel =
            inPaymentGrace && user.subscriptionAccessUntil
              ? `Grace ends: ${new Date(user.subscriptionAccessUntil).toLocaleString()}`
              : null;

          return (
            <div
              className="admin-status-cell"
              style={{
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: "0.35rem",
              }}
            >
              <span
                className="admin-subscription-badge"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!user.isAnonymous && user.userId) {
                    navigate(`/admin/subscription-history/${encodeURIComponent(user.userId)}`);
                  }
                }}
                title={
                  user.isAnonymous
                    ? "Subscription history is unavailable for anonymous users"
                    : [
                        "View full subscription history",
                        paymentSubtitle,
                        graceEndsLabel,
                        user.paymentFailedAt
                          ? `Failed: ${new Date(user.paymentFailedAt).toLocaleString()}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")
                }
                style={{
                  display: "inline-block",
                  padding: "0.25rem 0.75rem",
                  borderRadius: "0.375rem",
                  fontSize: "0.875rem",
                  fontWeight: "600",
                  cursor: user.isAnonymous ? "not-allowed" : "pointer",
                  background: showPaymentCritical
                    ? darkMode
                      ? "rgba(239, 68, 68, 0.15)"
                      : "rgba(239, 68, 68, 0.1)"
                    : showPaymentWarning
                      ? darkMode
                        ? "rgba(245, 158, 11, 0.15)"
                        : "rgba(245, 158, 11, 0.12)"
                      : isTrialing
                        ? darkMode
                          ? "rgba(59, 130, 246, 0.15)"
                          : "rgba(59, 130, 246, 0.1)"
                        : isPaid
                          ? darkMode
                            ? "rgba(34, 197, 94, 0.15)"
                            : "rgba(34, 197, 94, 0.1)"
                          : darkMode
                            ? "rgba(156, 163, 175, 0.15)"
                            : "rgba(156, 163, 175, 0.1)",
                  color: showPaymentCritical
                    ? "#dc2626"
                    : showPaymentWarning
                      ? "#d97706"
                      : isTrialing
                        ? "#3b82f6"
                        : isPaid
                          ? "#22c55e"
                          : darkMode
                            ? "#9ca3af"
                            : "#6b7280",
                  border: `1px solid ${showPaymentCritical
                    ? darkMode
                      ? "rgba(239, 68, 68, 0.35)"
                      : "rgba(239, 68, 68, 0.25)"
                    : showPaymentWarning
                      ? darkMode
                        ? "rgba(245, 158, 11, 0.35)"
                        : "rgba(245, 158, 11, 0.25)"
                      : isTrialing
                        ? darkMode
                          ? "rgba(59, 130, 246, 0.3)"
                          : "rgba(59, 130, 246, 0.2)"
                        : isPaid
                          ? darkMode
                            ? "rgba(34, 197, 94, 0.3)"
                            : "rgba(34, 197, 94, 0.2)"
                          : darkMode
                            ? "rgba(156, 163, 175, 0.3)"
                            : "rgba(156, 163, 175, 0.2)"
                    }`,
                }}
              >
                {displayStatus}
              </span>
              {paymentSubtitle && (
                <div
                  role="status"
                  style={{
                    maxWidth: "220px",
                    padding: "0.35rem 0.5rem",
                    borderRadius: "0.375rem",
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    lineHeight: 1.3,
                    textAlign: "center",
                    color: paymentAccessBlocked ? "#b91c1c" : "#b45309",
                    background: paymentAccessBlocked
                      ? darkMode
                        ? "rgba(239, 68, 68, 0.15)"
                        : "rgba(239, 68, 68, 0.1)"
                      : darkMode
                        ? "rgba(245, 158, 11, 0.12)"
                        : "rgba(254, 243, 199, 0.9)",
                    border: `1px solid ${
                      paymentAccessBlocked
                        ? darkMode
                          ? "rgba(239, 68, 68, 0.35)"
                          : "rgba(239, 68, 68, 0.25)"
                        : darkMode
                          ? "rgba(245, 158, 11, 0.35)"
                          : "rgba(245, 158, 11, 0.35)"
                    }`,
                  }}
                >
                  {paymentSubtitle}
                  {inPaymentGrace && graceEndsLabel && (
                    <div
                      style={{
                        marginTop: "0.25rem",
                        fontSize: "0.65rem",
                        fontWeight: 500,
                        opacity: 0.9,
                      }}
                    >
                      {graceEndsLabel}
                    </div>
                  )}
                </div>
              )}
              {paymentAlert && (
                <div
                  role="alert"
                  title={paymentAlert}
                  style={{
                    maxWidth: "220px",
                    padding: "0.35rem 0.5rem",
                    borderRadius: "0.375rem",
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    lineHeight: 1.3,
                    color: "#b91c1c",
                    background: darkMode
                      ? "rgba(239, 68, 68, 0.15)"
                      : "rgba(239, 68, 68, 0.1)",
                    border: `1px solid ${darkMode ? "rgba(239, 68, 68, 0.35)" : "rgba(239, 68, 68, 0.25)"}`,
                  }}
                >
                  Payment retry failed: {paymentAlert}
                </div>
              )}
            </div>
          );
        },
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.subscriptionPlan || "free";
          const b = rowB.original.subscriptionPlan || "free";
          const aIsPaid = a === "essentials" || a === "professional";
          const bIsPaid = b === "essentials" || b === "professional";
          if (aIsPaid === bIsPaid) {
            return a.localeCompare(b);
          }
          return aIsPaid ? 1 : -1;
        },
      }),
      columnHelper.accessor("authProvider", {
        header: "Auth Provider",
        cell: (info) => {
          const provider = info.getValue() || "local";
          return (
            <span
              className="admin-auth-badge"
              style={{
                display: "inline-block",
                textAlign: "center",
                width: "100%",
              }}
            >
              {provider === "local" ? "email" : provider}
            </span>
          );
        },
      }),
      columnHelper.accessor("createdAt", {
        header: "Created At",
        cell: (info) => (
          <div
            className="admin-date"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.25rem",
            }}
          >
            <Calendar size={14} />
            {formatDate(info.getValue())}
          </div>
        ),
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.createdAt
            ? new Date(rowA.original.createdAt).getTime()
            : 0;
          const b = rowB.original.createdAt
            ? new Date(rowB.original.createdAt).getTime()
            : 0;
          return a - b;
        },
      }),
      columnHelper.accessor("heardAboutUs", {
        header: "Heard About Us",
        cell: (info) => {
          const value = info.getValue();
          return (
            <div
              style={{
                textAlign: "center",
                fontSize: "0.82rem",
                color: value
                  ? darkMode ? "#e2e8f0" : "#374151"
                  : darkMode ? "#64748b" : "#9ca3af",
                fontStyle: value ? "normal" : "italic",
              }}
            >
              {value || "N/A"}
            </div>
          );
        },
        enableSorting: false,
      }),
      columnHelper.accessor("aiActionsUsed", {

        id: "aiActions",
        header: "AI Action Details",
        cell: (info) => {
          const user = info.row.original;
          const subscriptionPlan = user.subscriptionPlan || "free";
          const isTrialing = user.subscriptionStatus === "trialing";
          const hasPaidPlan =
            subscriptionPlan === "essentials" ||
            subscriptionPlan === "professional";
          const monthlyLimit = user.aiMonthlyLimit || 0;
          const useTrialCounters =
            isTrialing || (hasPaidPlan && monthlyLimit <= 20);
          const ACTIONS_LIMIT = useTrialCounters ? 20 : monthlyLimit || 500;
          const actionsUsed = useTrialCounters
            ? user.freeTrialTokensUsed || 0
            : user.aiActionsUsed || 0;

          return (
            <div className="admin-token-limit" style={{ textAlign: "center" }}>
              <div
                style={{
                  marginBottom: "0.5rem",
                  fontSize: "0.875rem",
                  color: darkMode ? "#cbd5e1" : "#374151",
                  textAlign: "center",
                }}
              >
                Monthly Limit:{" "}
                <strong style={{ color: darkMode ? "#60a5fa" : "#2563eb" }}>
                  {ACTIONS_LIMIT >= 1000000
                    ? "Unlimited"
                    : formatNumber(ACTIONS_LIMIT)}
                </strong>{" "}
                | Used:{" "}
                <strong style={{ color: darkMode ? "#60a5fa" : "#2563eb" }}>
                  {formatNumber(actionsUsed)}
                </strong>
              </div>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddTokensClick(user);
                  }}
                  disabled={addingTokens.has(user.userId) || user.isAnonymous}
                  style={{
                    background: darkMode
                      ? "rgba(34, 197, 94, 0.15)"
                      : "rgba(34, 197, 94, 0.1)",
                    color: "#22c55e",
                    border: `1px solid ${darkMode ? "rgba(34, 197, 94, 0.3)" : "rgba(34, 197, 94, 0.2)"}`,
                    borderRadius: "0.375rem",
                    padding: "0.25rem 0.5rem",
                    fontSize: "0.7rem",
                    fontWeight: "600",
                    cursor:
                      addingTokens.has(user.userId) || user.isAnonymous
                        ? "not-allowed"
                        : "pointer",
                    transition: "all 0.2s ease",
                    opacity:
                      addingTokens.has(user.userId) || user.isAnonymous
                        ? 0.6
                        : 1,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.25rem",
                    whiteSpace: "nowrap",
                  }}
                  onMouseEnter={(e) => {
                    if (!addingTokens.has(user.userId) && !user.isAnonymous) {
                      e.currentTarget.style.background = darkMode
                        ? "rgba(34, 197, 94, 0.25)"
                        : "rgba(34, 197, 94, 0.15)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = darkMode
                      ? "rgba(34, 197, 94, 0.15)"
                      : "rgba(34, 197, 94, 0.1)";
                  }}
                >
                  {addingTokens.has(user.userId) ? (
                    <>
                      <Loader className="spin" size={10} />
                      Adding...
                    </>
                  ) : (
                    <>+ Add AI Actions</>
                  )}
                </button>
              </div>
            </div>
          );
        },
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          const uA = rowA.original;
          const uB = rowB.original;
          const a = uA.subscriptionStatus === "trialing" ? (uA.freeTrialTokensUsed || 0) : (uA.aiActionsUsed || 0);
          const b = uB.subscriptionStatus === "trialing" ? (uB.freeTrialTokensUsed || 0) : (uB.aiActionsUsed || 0);
          return a - b;
        },
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (info) => {
          const user = info.row.original;
          const isCurrentUser = currentUser?.id === user.userId;

          return (
            <div
              className="admin-actions-cell"
              style={{
                position: "relative",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {!isCurrentUser ? (
                  <button
                    className="admin-menu-trigger"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (openMenuId === user.userId) {
                        closeActionsMenu();
                      } else {
                        setActionsMenuLayout(
                          computeAdminActionsMenuLayout(e.currentTarget),
                        );
                        setOpenMenuId(user.userId);
                      }
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "0.5rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: darkMode ? "#fff" : "#333",
                      borderRadius: "0.25rem",
                      transition: "background 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = darkMode
                        ? "rgba(255, 255, 255, 0.1)"
                        : "rgba(0, 0, 0, 0.05)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "none";
                    }}
                  >
                    <MoreVertical size={20} />
                  </button>
              ) : (
                <span
                  className="admin-self-indicator"
                  title="You cannot modify your own account"
                >
                  (You)
                </span>
              )}
            </div>
          );
        },
      }),
    ],
    [
      currentUser,
      updatingUsers,
      deactivatingUsers,
      deletingUsers,
      navigate,
      updateAdminStatus,
      handleDeactivateUser,
      handleAdminCancelSubscription,
      handleDeleteClick,
      formatDate,
      formatNumber,
      getTotalTokensForUser,
      openMenuId,
      darkMode,
      addingTokens,
      cancelingSubscriptions,
      closeActionsMenu,
    ],
  );

  // Filter data based on search and filters
  const filteredData = useMemo(() => {
    let result = [...users];

    // Text search on name/email
    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      result = result.filter(
        (user) =>
          (user.name || "").toLowerCase().includes(term) ||
          (user.email || "").toLowerCase().includes(term),
      );
    }

    // Admin filter
    if (adminFilter !== "all") {
      const wantAdmin = adminFilter === "admin";
      result = result.filter((user) => (user.is_Admin === true) === wantAdmin);
    }

    // Auth provider filter
    if (providerFilter !== "all") {
      result = result.filter((user) => {
        const provider = (user.authProvider || "local").toLowerCase();
        if (providerFilter === "local") return provider === "local";
        if (providerFilter === "google") return provider === "google";
        return provider !== "local" && provider !== "google";
      });
    }

    return result;
  }, [users, searchTerm, adminFilter, providerFilter]);

  // Reset pagination when filters change
  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [searchTerm, adminFilter, providerFilter]);

  // Sync pageSizeInput with pagination.pageSize
  useEffect(() => {
    setPageSizeInput(String(pagination.pageSize));
  }, [pagination.pageSize]);

  // Set up TanStack Table
  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      sorting,
      pagination,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  if (authLoading || refreshingAuth) {
    return (
      <div className={`admin-container ${darkMode ? "dark" : ""}`}>
        <Header />
        <div className="admin-content">
          <div className="admin-loading">
            <Loader className="spin" size={32} />
            <p>Verifying admin access...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  if (!isAdmin) {
    return (
      <div className={`admin-container ${darkMode ? "dark" : ""}`}>
        <Header />
        <div className="admin-content">
          <div className="admin-error">
            <h2>Access Denied</h2>
            <p>
              You do not have admin privileges. If you were recently granted
              admin access, please log out and log back in.
            </p>
            <button
              onClick={() => {
                verifyToken();
                setTimeout(() => {
                  if (!isAdmin) {
                    showWarning(
                      "Admin access not found. Please log out and log back in after admin status has been granted.",
                    );
                  }
                }, 1000);
              }}
              className="admin-refresh-btn"
              style={{ marginTop: "1rem" }}
            >
              Refresh Admin Status
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`admin-container ${darkMode ? "dark" : ""}`}>
      <SEO title="Admin Dashboard" path="/admin" description="LawFusion admin panel." noIndex={true} />
      <Header />
      <div className="admin-content">
        <div className="admin-header">
          <div className="admin-header-title">
            <Users className="admin-icon" size={32} />
            <h1>Admin Dashboard</h1>
          </div>
          <div
            style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}
          >
            <button
              onClick={handleInviteUserClick}
              className="admin-refresh-btn"
              style={{
                background: darkMode
                  ? "rgba(37, 99, 235, 0.15)"
                  : "rgba(37, 99, 235, 0.1)",
                color: "#2563eb",
                border: `1px solid ${darkMode ? "rgba(37, 99, 235, 0.3)" : "rgba(37, 99, 235, 0.2)"}`,
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <Mail size={18} />
              Invite User
            </button>
            <button
              onClick={handleInviteTesterClick}
              className="admin-refresh-btn"
              style={{
                background: darkMode
                  ? "rgba(34, 197, 94, 0.15)"
                  : "rgba(34, 197, 94, 0.1)",
                color: "#22c55e",
                border: `1px solid ${darkMode ? "rgba(34, 197, 94, 0.3)" : "rgba(34, 197, 94, 0.2)"}`,
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <Mail size={18} />
              Invite Tester
            </button>
            <button
              onClick={fetchUsers}
              className="admin-refresh-btn"
              disabled={loading}
            >
              {loading ? <Loader className="spin" size={18} /> : "Refresh"}
            </button>
          </div>
        </div>

        <div className="admin-main-tabs" role="tablist" aria-label="Admin sections">
          <button
            type="button"
            role="tab"
            aria-selected={adminMainTab === "users"}
            className={`admin-main-tab ${adminMainTab === "users" ? "active" : ""}`}
            onClick={() => setAdminMainTab("users")}
          >
            <Users size={18} />
            Users
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={adminMainTab === "tokens"}
            className={`admin-main-tab ${adminMainTab === "tokens" ? "active" : ""}`}
            onClick={() => setAdminMainTab("tokens")}
          >
            <BarChart3 size={18} />
            Token analytics
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={adminMainTab === "usage"}
            className={`admin-main-tab ${adminMainTab === "usage" ? "active" : ""}`}
            onClick={() => setAdminMainTab("usage")}
          >
            <PieChart size={18} />
            Usage &amp; cost
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={adminMainTab === "directory"}
            className={`admin-main-tab ${adminMainTab === "directory" ? "active" : ""}`}
            onClick={() => setAdminMainTab("directory")}
          >
            <BookOpen size={18} />
            Directory submissions
          </button>
        </div>

        {error && adminMainTab === "users" && (
          <div className="admin-error">
            <p>{error}</p>
          </div>
        )}

        {adminMainTab === "tokens" && (
          <div className="ta-dashboard">
            <div className="ta-top-bar">
              <h2 className="ta-title">Token analytics</h2>
              <div className="ta-top-controls">
                <div className="ta-pill-group" role="group" aria-label="Date range">
                  {[
                    { key: "7", label: "7 days" },
                    { key: "30", label: "30 days" },
                    { key: "90", label: "90 days" },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      className={
                        tokenRangePreset === key ? "active" : ""
                      }
                      onClick={() => applyTokenPresetDays(Number(key))}
                      disabled={tokenLoading}
                    >
                      {label}
                    </button>
                  ))}
                  <button
                    type="button"
                    className={
                      tokenRangePreset === "custom" ? "active" : ""
                    }
                    onClick={() => setTokenRangePreset("custom")}
                    disabled={tokenLoading}
                  >
                    Custom
                  </button>
                  <button
                    type="button"
                    className="ta-apply-btn"
                    onClick={() => fetchTokenAnalytics()}
                    disabled={tokenLoading}
                  >
                    {tokenLoading ? (
                      <Loader className="spin" size={16} />
                    ) : (
                      "Refresh Token Analytics"
                    )}
                  </button>
                </div>

                {tokenRangePreset === "custom" && (
                  <div className="ta-date-inputs">
                    <CustomDatePicker
                      value={tokenStart}
                      onChange={setTokenStart}
                      label="From date"
                      darkMode={darkMode}
                    />
                    <span className="ta-date-to">to</span>
                    <CustomDatePicker
                      value={tokenEnd}
                      onChange={setTokenEnd}
                      label="To date"
                      darkMode={darkMode}
                    />
                  </div>
                )}
              </div>
            </div>

            {tokenError && (
              <div className="admin-error">
                <p>{tokenError}</p>
              </div>
            )}

            {tokenLoading && (
              <div className="admin-loading ta-loading">
                <Loader className="spin" size={32} />
                <p>Loading token analytics…</p>
              </div>
            )}

            {tokenAnalytics && !tokenLoading && (() => {
              const kpis = tokenAnalytics.kpis || {};
              const byModel = tokenAnalytics.byModel || [];
              const byTier = tokenAnalytics.byTier || [];
              const totalCostAll = Number(kpis.totalCostUsd || 0);
              const grandIn = Number(
                kpis.totalInputCostUsd ?? 0,
              );
              const grandOut = Number(
                kpis.totalOutputCostUsd ?? 0,
              );

              // UI normalization: merge all Anthropic/Claude variants into one
              // displayed row ("Claude") so the table never shows multiple Claudes.
              const normalizedByModel = (() => {
                const map = new Map();
                const isClaude = (row) => {
                  const model = String(row?.model || "").toLowerCase();
                  const mv = String(row?.primaryModelVersion || "").toLowerCase();
                  return model === "claude" || model.includes("claude") || mv.startsWith("claude-");
                };

                for (const row of byModel) {
                  const key = isClaude(row) ? "Claude" : String(row.model || "");
                  const existing = map.get(key);
                  if (!existing) {
                    map.set(key, { ...row, model: key });
                    continue;
                  }
                  existing.inputTokens += Number(row.inputTokens || 0);
                  existing.outputTokens += Number(row.outputTokens || 0);
                  existing.totalTokens += Number(row.totalTokens || 0);
                  existing.costUsd += Number(row.costUsd || 0);
                  existing.inputCostUsd += Number(row.inputCostUsd || 0);
                  existing.outputCostUsd += Number(row.outputCostUsd || 0);
                  existing.calls += Number(row.calls || 0);

                  // Prefer Sonnet 4.5 when present; otherwise keep the first available non-empty value.
                  if (key === "Claude") {
                    const pv = String(row.primaryModelVersion || "").trim();
                    if (pv && pv !== "(not reported)" && pv.toLowerCase().includes("claude-sonnet-4-5")) {
                      existing.primaryModelVersion = pv;
                    } else if (
                      (!existing.primaryModelVersion || existing.primaryModelVersion === "(not reported)") &&
                      pv &&
                      pv !== "(not reported)"
                    ) {
                      existing.primaryModelVersion = pv;
                    }
                  }
                }

                return Array.from(map.values()).sort(
                  (a, b) => Number(b.costUsd || 0) - Number(a.costUsd || 0),
                );
              })();

              const modelTotalInTok = normalizedByModel.reduce(
                (s, r) => s + (r.inputTokens || 0),
                0,
              );
              const modelTotalOutTok = normalizedByModel.reduce(
                (s, r) => s + (r.outputTokens || 0),
                0,
              );
              const modelTotalInCost = normalizedByModel.reduce(
                (s, r) => s + Number(r.inputCostUsd || 0),
                0,
              );
              const modelTotalOutCost = normalizedByModel.reduce(
                (s, r) => s + Number(r.outputCostUsd || 0),
                0,
              );
              const tierTotalCost = byTier.reduce(
                (s, r) => s + Number(r.costUsd || 0),
                0,
              );
              return (
                <>
                  <div className="ta-stat-grid">
                    <div className="ta-stat-card">
                      <div className="ta-stat-label">Total input tokens</div>
                      <div className="ta-stat-val">
                        {formatTokensShort(kpis.totalInputTokens)}
                      </div>
                      <div className="ta-stat-sub">
                        All tiers · selected range
                      </div>
                    </div>
                    <div className="ta-stat-card">
                      <div className="ta-stat-label">Total output tokens</div>
                      <div className="ta-stat-val">
                        {formatTokensShort(kpis.totalOutputTokens)}
                      </div>
                      <div className="ta-stat-sub">
                        All tiers · selected range
                      </div>
                    </div>
                    <div className="ta-stat-card">
                      <div className="ta-stat-label">Total tokens</div>
                      <div className="ta-stat-val">
                        {formatTokensShort(kpis.totalTokens)}
                      </div>
                      <div className="ta-stat-sub">
                        Combined · selected range
                      </div>
                    </div>
                    <div className="ta-stat-card">
                      <div className="ta-stat-label">Total cost</div>
                      <div className="ta-stat-val">
                        {formatUsdCompact(kpis.totalCostUsd)}
                      </div>
                      <div className="ta-stat-sub">Input + output</div>
                    </div>
                    <div className="ta-stat-card ta-stat-card-highlight">
                      <div className="ta-stat-label">
                        <span className="ta-tooltip-wrap">
                          Total AI Actions
                          <i className="ta-tooltip-icon">i</i>
                          <span className="ta-tooltip-box">
                            AI responses generated across all models and rounds
                          </span>
                        </span>
                      </div>
                      <div className="ta-stat-val">
                        {formatNumber(kpis.totalAiActions ?? 0)}
                      </div>
                      <div className="ta-stat-sub">
                        All tiers · selected range
                      </div>
                    </div>
                  </div>

                  <div className="ta-section-label">Token usage by model</div>
                  <div className="ta-table-card">
                    <table className="ta-table">
                      <thead>
                        <tr>
                          <th style={{ width: "22%" }}>Model</th>
                          <th style={{ width: "15%" }}>Input tokens</th>
                          <th style={{ width: "13%" }}>Input cost</th>
                          <th style={{ width: "15%" }}>Output tokens</th>
                          <th style={{ width: "13%" }}>Output cost</th>
                          <th style={{ width: "12%" }}>Total cost</th>
                          <th style={{ width: "10%" }}>Cost share</th>
                        </tr>
                      </thead>
                      <tbody>
                        {normalizedByModel.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="ta-empty-cell">
                              No token events in this range.
                            </td>
                          </tr>
                        ) : (
                          normalizedByModel.map((row) => {
                            const share =
                              totalCostAll > 0
                                ? (100 * Number(row.costUsd || 0)) /
                                totalCostAll
                                : 0;
                            const ver = getCurrentModelVersion(
                              row.model,
                              row.primaryModelVersion ||
                              (row.modelVersions || [])[0]?.version,
                            );
                            return (
                              <tr key={row.model}>
                                <td>
                                  <div className="ta-model-tag">
                                    <span
                                      className={`ta-model-name ${modelAnalyticsClass(row.model)}`}
                                    >
                                      {row.model}
                                    </span>
                                    {ver ? (
                                      <span className="ta-model-ver">{ver}</span>
                                    ) : null}
                                  </div>
                                </td>
                                <td>{formatNumber(row.inputTokens)}</td>
                                <td>{formatUsdCompact(row.inputCostUsd, 4)}</td>
                                <td>{formatNumber(row.outputTokens)}</td>
                                <td>
                                  {formatUsdCompact(row.outputCostUsd, 4)}
                                </td>
                                <td>{formatUsdCompact(row.costUsd, 4)}</td>
                                <td>
                                  <div className="ta-bar-wrap">
                                    <div className="ta-bar-bg">
                                      <div
                                        className={`ta-bar-fill ${modelAnalyticsClass(row.model)}`}
                                        style={{
                                          width: `${Math.min(100, share)}%`,
                                        }}
                                      />
                                    </div>
                                    <span className="ta-bar-pct">
                                      {share.toFixed(1)}%
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                        {normalizedByModel.length > 0 ? (
                          <tr className="ta-total-row">
                            <td>Total</td>
                            <td>{formatNumber(modelTotalInTok)}</td>
                            <td>{formatUsdCompact(modelTotalInCost, 4)}</td>
                            <td>{formatNumber(modelTotalOutTok)}</td>
                            <td>{formatUsdCompact(modelTotalOutCost, 4)}</td>
                            <td>{formatUsdCompact(totalCostAll, 4)}</td>
                            <td>100%</td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>

                  <div className="ta-section-label">Token usage by tier</div>
                  <div className="ta-table-card">
                    <table className="ta-table">
                      <thead>
                        <tr>
                          <th style={{ width: "13%" }}>Tier</th>
                          <th style={{ width: "7%" }}>Users</th>
                          <th style={{ width: "10%" }}>
                            <span className="ta-tooltip-wrap">
                              AI Actions
                              <i className="ta-tooltip-icon">i</i>
                              <span className="ta-tooltip-box">
                                AI responses generated across all models and rounds
                              </span>
                            </span>
                          </th>
                          <th style={{ width: "13%" }}>Input tokens</th>
                          <th style={{ width: "11%" }}>Input cost</th>
                          <th style={{ width: "13%" }}>Output tokens</th>
                          <th style={{ width: "11%" }}>Output cost</th>
                          <th style={{ width: "10%" }}>Total cost</th>
                          <th style={{ width: "8%" }}>% of total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {byTier.length === 0 ? (
                          <tr>
                            <td colSpan={9} className="ta-empty-cell">
                              No tier data in this range.
                            </td>
                          </tr>
                        ) : (
                          byTier.map((row) => {
                            const pct =
                              tierTotalCost > 0
                                ? (100 * Number(row.costUsd || 0)) /
                                tierTotalCost
                                : 0;
                            return (
                              <tr key={row.tierAtTime}>
                                <td>
                                  <span
                                    className={`ta-tier-badge ${tierAnalyticsClass(row.tierAtTime)}`}
                                  >
                                    {tierDisplayLabel(row.tierAtTime)}
                                  </span>
                                </td>
                                <td>
                                  {formatNumber(row.distinctUserCount ?? 0)}
                                </td>
                                <td className="ta-blue">
                                  {formatNumber(row.aiActions ?? 0)}
                                </td>
                                <td>{formatNumber(row.inputTokens)}</td>
                                <td>
                                  {formatUsdCompact(row.inputCostUsd, 4)}
                                </td>
                                <td>{formatNumber(row.outputTokens)}</td>
                                <td>
                                  {formatUsdCompact(row.outputCostUsd, 4)}
                                </td>
                                <td>{formatUsdCompact(row.costUsd, 4)}</td>
                                <td>
                                  <div className="ta-bar-wrap">
                                    <div className="ta-bar-bg">
                                      <div
                                        className={`ta-bar-fill ${tierAnalyticsClass(row.tierAtTime)}`}
                                        style={{
                                          width: `${Math.min(100, pct)}%`,
                                        }}
                                      />
                                    </div>
                                    <span className="ta-bar-pct">
                                      {pct.toFixed(1)}%
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                        {byTier.length > 0 ? (
                          <tr className="ta-total-row">
                            <td>Total</td>
                            <td
                              title="User counts per tier are distinct within each tier; they are not additive across tiers."
                            >
                              —
                            </td>
                            <td className="ta-blue">
                              {formatNumber(
                                byTier.reduce(
                                  (s, r) => s + Number(r.aiActions || 0),
                                  0,
                                ),
                              )}
                            </td>
                            <td>
                              {formatNumber(
                                byTier.reduce(
                                  (s, r) => s + (r.inputTokens || 0),
                                  0,
                                ),
                              )}
                            </td>
                            <td>
                              {formatUsdCompact(
                                byTier.reduce(
                                  (s, r) => s + Number(r.inputCostUsd || 0),
                                  0,
                                ),
                                4,
                              )}
                            </td>
                            <td>
                              {formatNumber(
                                byTier.reduce(
                                  (s, r) => s + (r.outputTokens || 0),
                                  0,
                                ),
                              )}
                            </td>
                            <td>
                              {formatUsdCompact(
                                byTier.reduce(
                                  (s, r) => s + Number(r.outputCostUsd || 0),
                                  0,
                                ),
                                4,
                              )}
                            </td>
                            <td>{formatUsdCompact(tierTotalCost, 4)}</td>
                            <td>{tierTotalCost > 0 ? "100%" : "0%"}</td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>

                  <div className="ta-grand-total">
                    <div className="ta-grand-label">
                      Grand total · all tiers · all models
                    </div>
                    <div className="ta-grand-items">
                      <div className="ta-grand-item">
                        <div className="ta-grand-gl">AI Actions</div>
                        <div className="ta-grand-gv ta-grand-gv-blue">
                          {formatNumber(kpis.totalAiActions ?? 0)}
                        </div>
                      </div>
                      <div className="ta-grand-divider" aria-hidden />
                      <div className="ta-grand-item">
                        <div className="ta-grand-gl">Input cost</div>
                        <div className="ta-grand-gv">
                          {formatUsdCompact(grandIn)}
                        </div>
                      </div>
                      <div className="ta-grand-divider" aria-hidden />
                      <div className="ta-grand-item">
                        <div className="ta-grand-gl">Output cost</div>
                        <div className="ta-grand-gv">
                          {formatUsdCompact(grandOut)}
                        </div>
                      </div>
                      <div className="ta-grand-divider" aria-hidden />
                      <div className="ta-grand-item">
                        <div className="ta-grand-gl">Total cost</div>
                        <div className="ta-grand-gv ta-grand-gv-green">
                          {formatUsdCompact(kpis.totalCostUsd)}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="ta-section-label">Usage by mode</div>
                  <div className="ta-table-card">
                    <table className="ta-table">
                      <thead>
                        <tr>
                          <th style={{ width: "50%" }}>Mode</th>
                          <th style={{ width: "25%" }}>Runs</th>
                          <th style={{ width: "25%" }}>AI Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(tokenAnalytics.usageByModeGlobal || []).length === 0 ? (
                          <tr>
                            <td colSpan={3} className="ta-empty-cell">
                              No mode data in this range.
                            </td>
                          </tr>
                        ) : (
                          (tokenAnalytics.usageByModeGlobal || []).map((row) => (
                            <tr key={row.mode}>
                              <td>{row.mode}</td>
                              <td>{formatNumber(row.runs ?? 0)}</td>
                              <td className="ta-blue">
                                {formatNumber(row.aiActions ?? 0)}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  <p className="ta-footnote">
                    Costs use per-model rates in backend MODEL_COSTS_PER_1M from
                    actual input/output tokens logged per request. Rows with
                    unknown models show $0 cost until a rate is configured.
                  </p>
                </>
              );
            })()}
          </div>
        )}

        {adminMainTab === "usage" && (
          <div className="uc-dashboard">
            <div className="uc-header">
              <div className="uc-header-left">
                <PieChart size={22} className="uc-header-icon" aria-hidden />
                <span className="uc-header-title">Usage &amp; cost</span>
              </div>
              <div className="uc-header-actions">
                <button
                  type="button"
                  className="uc-btn uc-btn-primary"
                  onClick={() => fetchUsageCost()}
                  disabled={usageCostLoading}
                >
                  {usageCostLoading ? (
                    <Loader className="spin" size={16} />
                  ) : (
                    "Refresh Cost & Usage"
                  )}
                </button>
              </div>
            </div>

            <div className="uc-top-metrics">
              {usageCostData?.kpis && (
                <>
                  <div className="uc-top-metric">
                    <div className="uc-top-metric-label">Total users</div>
                    <div className="uc-top-metric-value">
                      {formatNumber(usageCostData.kpis.totalPlatformUsers ?? 0)}
                    </div>
                  </div>
                  <div className="uc-top-metric">
                    <div className="uc-top-metric-label">Total token events</div>
                    <div className="uc-top-metric-value">
                      {formatNumber(usageCostData.kpis.totalTokenEvents ?? 0)}
                    </div>
                  </div>
                  <div className="uc-top-metric">
                    <div className="uc-top-metric-label">Paid &amp; trial cost</div>
                    <div className="uc-top-metric-value">
                      {formatUsdCompact(usageCostData.kpis.paidAndTrialCostUsd)}
                    </div>
                  </div>
                  <div className="uc-top-metric">
                    <div className="uc-top-metric-label">Free tier cost</div>
                    <div className="uc-top-metric-value">
                      {formatUsdCompact(usageCostData.kpis.freeTierCostUsd)}
                    </div>
                  </div>
                  <div className="uc-top-metric">
                    <div className="uc-top-metric-label">Total cost</div>
                    <div className="uc-top-metric-value">
                      {formatUsdCompact(usageCostData.kpis.totalCostUsd)}
                    </div>
                  </div>
                </>
              )}
              <div className="uc-date-controls">
                <select
                  className="uc-select"
                  value={usageRangePreset}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "7") applyUsagePresetDays(7);
                    else if (v === "30") applyUsagePresetDays(30);
                    else if (v === "90") applyUsagePresetDays(90);
                    else if (v === "month") applyUsageThisMonth();
                    else setUsageRangePreset("custom");
                  }}
                  aria-label="Date range preset"
                >
                  <option value="7">Last 7 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="90">Last 90 days</option>
                  <option value="month">This month</option>
                  <option value="custom">Custom range</option>
                </select>
                {usageRangePreset === "custom" && (
                  <>
                    <CustomDatePicker
                      value={usageStart}
                      onChange={setUsageStart}
                      label="From date"
                      darkMode={darkMode}
                      inputClassName="uc-date-input"
                      iconClassName="uc-input-icon"
                    />
                    <CustomDatePicker
                      value={usageEnd}
                      onChange={setUsageEnd}
                      label="To date"
                      darkMode={darkMode}
                      inputClassName="uc-date-input"
                      iconClassName="uc-input-icon"
                    />
                    <button
                      type="button"
                      className="uc-btn uc-btn-primary uc-btn-small"
                      onClick={() => fetchUsageCost()}
                      disabled={usageCostLoading}
                    >
                      Apply
                    </button>
                  </>
                )}
              </div>
            </div>

            {usageCostError && (
              <div className="admin-error">
                <p>{usageCostError}</p>
              </div>
            )}

            {usageCostLoading && !usageCostData && (
              <div className="admin-loading uc-loading">
                <Loader className="spin" size={32} />
                <p>Loading usage &amp; cost…</p>
              </div>
            )}

            {usageCostData && (
              <>
                <div className="uc-search-bar">
                  <input
                    type="search"
                    className="uc-search-input"
                    placeholder="Search by name or email..."
                    value={usageSearch}
                    onChange={(e) => setUsageSearch(e.target.value)}
                    aria-label="Filter users"
                  />
                </div>

                <div className="uc-table-wrap">
                  <table className="uc-table">
                    <thead>
                      <tr>
                        <th className="uc-th-chevron" aria-hidden />
                        <th
                          className={`uc-th-sort ${usageSort.key === "name" ? `uc-th-${usageSort.dir}` : ""}`}
                          onClick={() => toggleUsageSort("name")}
                        >
                          Name <i className="uc-sort-icon" />
                        </th>
                        <th>Email</th>
                        <th>Role</th>
                        <th
                          className={`uc-th-sort ${usageSort.key === "plan" ? `uc-th-${usageSort.dir}` : ""}`}
                          onClick={() => toggleUsageSort("plan")}
                        >
                          Plan <i className="uc-sort-icon" />
                        </th>
                        <th
                          className={`uc-th-sort ${usageSort.key === "actions" ? `uc-th-${usageSort.dir}` : ""}`}
                          onClick={() => toggleUsageSort("actions")}
                        >
                          Token events <i className="uc-sort-icon" />
                        </th>
                        <th
                          className={`uc-th-sort ${usageSort.key === "cost" ? `uc-th-${usageSort.dir}` : ""}`}
                          onClick={() => toggleUsageSort("cost")}
                        >
                          Cost <i className="uc-sort-icon" />
                        </th>
                        <th
                          className={`uc-th-sort ${usageSort.key === "joined" ? `uc-th-${usageSort.dir}` : ""}`}
                          onClick={() => toggleUsageSort("joined")}
                        >
                          Joined <i className="uc-sort-icon" />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {usageProcessedRows.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="uc-no-results-cell">
                            No users match your search or had token usage in this
                            range.
                          </td>
                        </tr>
                      ) : (
                        usagePageRows.map((u) => {
                          const open = usageExpandedId === u.userId;
                          const avatarBg =
                            u.planBadgeClass === "pro"
                              ? "#E6F1FB"
                              : u.planBadgeClass === "ess"
                                ? "#EAF3DE"
                                : u.planBadgeClass === "trial"
                                  ? "#FAEEDA"
                                  : "#f0efe8";
                          const avatarColor =
                            u.planBadgeClass === "pro"
                              ? "#0C447C"
                              : u.planBadgeClass === "ess"
                                ? "#27500A"
                                : u.planBadgeClass === "trial"
                                  ? "#633806"
                                  : "#5a5a55";
                          return (
                            <React.Fragment key={u.userId}>
                              <tr
                                className={`uc-user-row ${open ? "open" : ""}`}
                                onClick={() =>
                                  setUsageExpandedId((id) =>
                                    id === u.userId ? null : u.userId,
                                  )
                                }
                              >
                                <td>
                                  <ChevronRight
                                    size={16}
                                    className={`uc-chevron ${open ? "open" : ""}`}
                                    aria-hidden
                                  />
                                </td>
                                <td>
                                  <div className="uc-name-cell">
                                    <div
                                      className="uc-avatar"
                                      style={{
                                        background: avatarBg,
                                        color: avatarColor,
                                      }}
                                    >
                                      {getUserInitials(u.name)}
                                    </div>
                                    <span>{u.name}</span>
                                  </div>
                                </td>
                                <td>{u.email}</td>
                                <td>{u.role}</td>
                                <td>
                                  <span
                                    className={`uc-badge uc-badge-${u.planBadgeClass}`}
                                  >
                                    {u.planLabel}
                                  </span>
                                </td>
                                <td>{formatNumber(u.aiActionsUsed)}</td>
                                <td>{formatUsdCompact(u.costUsd, 4)}</td>
                                <td>{u.joinedDisplay}</td>
                              </tr>
                              {open && (
                                <tr className="uc-expand-row">
                                  <td colSpan={8}>
                                    <div
                                      className="uc-expand-inner"
                                      onClick={(e) => e.stopPropagation()}
                                      role="presentation"
                                    >
                                      <div className="uc-metric-strip">
                                        <div className="uc-metric">
                                          <div className="uc-metric-label">
                                            Total tokens
                                          </div>
                                          <div className="uc-metric-value">
                                            {formatTokensShort(u.totalTokens)}
                                          </div>
                                          <div className="uc-metric-sub">
                                            In selected range
                                          </div>
                                        </div>
                                        <div className="uc-metric">
                                          <div className="uc-metric-label">
                                            Sessions (chats)
                                          </div>
                                          <div className="uc-metric-value">
                                            {formatNumber(u.sessionCount)}
                                          </div>
                                        </div>
                                        <div className="uc-metric">
                                          <div className="uc-metric-label">
                                            Debate events
                                          </div>
                                          <div className="uc-metric-value">
                                            {formatNumber(u.debateEvents)}
                                          </div>
                                        </div>
                                        <div className="uc-metric">
                                          <div className="uc-metric-label">
                                            Research events
                                          </div>
                                          <div className="uc-metric-value">
                                            {formatNumber(u.researchEvents)}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="uc-section-title">
                                        Cost by model
                                      </div>
                                      <div className="uc-inner-table">
                                        <table>
                                          <thead>
                                            <tr>
                                              <th>Model</th>
                                              <th>Input</th>
                                              <th>Output</th>
                                              <th>Cost</th>
                                              <th>Events</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {(u.models || []).length === 0 ? (
                                              <tr>
                                                <td colSpan={5} className="uc-no-activity">
                                                  No model breakdown
                                                </td>
                                              </tr>
                                            ) : (
                                              (u.models || []).map((m) => {
                                                const modelVersion = getCurrentModelVersion(
                                                  m.model,
                                                  m.modelVersion,
                                                );
                                                return (
                                                  <tr key={`${u.userId}-${m.model}`}>
                                                    <td>
                                                      <span
                                                        className={`ta-model-name ${modelAnalyticsClass(m.model)}`}
                                                      >
                                                        {m.model}
                                                      </span>
                                                      {modelVersion ? (
                                                        <span className="ta-model-ver">
                                                          {modelVersion}
                                                        </span>
                                                      ) : null}
                                                    </td>
                                                    <td>{formatNumber(m.inputTokens)}</td>
                                                    <td>{formatNumber(m.outputTokens)}</td>
                                                    <td>{formatUsdCompact(m.costUsd, 4)}</td>
                                                    <td>{formatNumber(m.eventCount)}</td>
                                                  </tr>
                                                );
                                              })
                                            )}
                                          </tbody>
                                        </table>
                                      </div>
                                      <div className="uc-section-title">
                                        Recent activity
                                      </div>
                                      {(u.activity || []).length === 0 ? (
                                        <p className="uc-no-activity">
                                          No recent activity rows in range.
                                        </p>
                                      ) : (
                                        <ul className="uc-activity-list">
                                          {(u.activity || []).map((a, i) => (
                                            <li key={`${u.userId}-act-${i}`}>
                                              <span className="uc-act-date">
                                                {a.atDisplay}
                                              </span>
                                              <span className="uc-act-label">
                                                {a.actionLabel}
                                              </span>
                                              <span className="uc-act-meta">
                                                {a.mode} · {formatTokensShort(a.tokens)}{" "}
                                                tokens
                                              </span>
                                            </li>
                                          ))}
                                        </ul>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                  <div className="uc-pagination">
                    <span>
                      Page {usagePage + 1} of {usageTotalPages} ·{" "}
                      {formatNumber(usageProcessedRows.length)} users
                    </span>
                    <div className="uc-page-btns">
                      <button
                        type="button"
                        className="uc-page-btn"
                        disabled={usagePage <= 0}
                        onClick={() => setUsagePage((p) => Math.max(0, p - 1))}
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        className="uc-page-btn"
                        disabled={usagePage >= usageTotalPages - 1}
                        onClick={() =>
                          setUsagePage((p) =>
                            Math.min(usageTotalPages - 1, p + 1),
                          )
                        }
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>

                <p className="uc-footnote">
                  Costs are computed from logged input/output tokens using exact
                  per-model rates. Paid &amp; trial vs free tier follows{" "}
                  <code>tierAtTime</code> on each request. Revenue / margin
                  requires billing data and is not shown yet.
                </p>
              </>
            )}
          </div>
        )}

        {adminMainTab === "directory" && (
          <div className="admin-directory-section" style={{ marginTop: "1rem" }}>
            {/* Header & Controls */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem", flexWrap: "wrap", gap: "1rem" }}>
              <div>
                <h2 style={{ fontSize: "1.25rem", fontWeight: 700, margin: 0, color: darkMode ? "#f8fafc" : "#0f172a" }}>
                  Free Directory Submissions
                </h2>
                <p style={{ fontSize: "0.875rem", color: darkMode ? "#94a3b8" : "#64748b", margin: "0.25rem 0 0" }}>
                  Review, approve, reject, or manage attorney directory listings.
                </p>
              </div>

              <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap" }}>
                <input
                  type="text"
                  placeholder="Search by name, email, city, state, role, practice area..."
                  value={dirSearchTerm}
                  onChange={(e) => {
                    setDirSearchTerm(e.target.value);
                    setDirPage(1);
                  }}
                  style={{
                    padding: "0.5rem 0.85rem",
                    borderRadius: "8px",
                    border: `1px solid ${darkMode ? "rgba(255,255,255,0.15)" : "#cbd5e1"}`,
                    background: darkMode ? "#1e293b" : "#fff",
                    color: darkMode ? "#f8fafc" : "#0f172a",
                    fontSize: "0.875rem",
                    minWidth: "260px",
                  }}
                />
                <button
                  type="button"
                  onClick={fetchDirectoryListings}
                  className="admin-refresh-btn"
                  disabled={dirLoading}
                >
                  {dirLoading ? <Loader className="spin" size={16} /> : "Refresh"}
                </button>
              </div>
            </div>

            {/* Status Pills Filter */}
            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.25rem" }}>
              {[
                { key: "all", label: `All (${dirCounts.all || 0})` },
                { key: "pending", label: `Pending (${dirCounts.pending || 0})` },
                { key: "approved", label: `Approved (${dirCounts.approved || 0})` },
                { key: "rejected", label: `Rejected (${dirCounts.rejected || 0})` },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    setDirStatusFilter(key);
                    setDirPage(1);
                  }}
                  style={{
                    padding: "0.45rem 0.9rem",
                    borderRadius: "20px",
                    fontSize: "0.825rem",
                    fontWeight: 600,
                    border: "none",
                    cursor: "pointer",
                    background: dirStatusFilter === key
                      ? "#2563eb"
                      : (darkMode ? "rgba(255,255,255,0.08)" : "#f1f5f9"),
                    color: dirStatusFilter === key
                      ? "#fff"
                      : (darkMode ? "#94a3b8" : "#475569"),
                    transition: "all 0.15s ease",
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Listings Table */}
            <div className="admin-table-container">
              {dirLoading ? (
                <div style={{ padding: "3rem", textAlign: "center" }}>
                  <Loader className="spin" size={28} />
                  <p style={{ marginTop: "0.5rem", color: darkMode ? "#94a3b8" : "#64748b" }}>Loading directory listings...</p>
                </div>
              ) : dirListings.length === 0 ? (
                <div style={{ padding: "3rem", textAlign: "center", color: darkMode ? "#94a3b8" : "#64748b" }}>
                  <BookOpen size={36} style={{ opacity: 0.5, marginBottom: "0.5rem" }} />
                  <p style={{ margin: 0, fontWeight: 600 }}>No directory listings found.</p>
                </div>
              ) : (
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Role</th>
                      <th>Email / Contact</th>
                      <th>Website</th>
                      <th>Practice Areas</th>
                      <th>Source</th>
                      <th>Submitted</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dirListings.map((item) => (
                      <tr key={item._id}>
                        <td>
                          <div style={{ fontWeight: 600, color: darkMode ? "#f8fafc" : "#0f172a" }}>
                            {item.firstName} {item.lastName}
                          </div>
                          {/* Featured comes with every paid plan, so the tier to
                              apply at approval is visible here rather than
                              needing a separate look at billing. */}
                          <div
                            title={
                              item.ownerHasPaidPlan
                                ? `On the ${item.ownerPlan || "paid"} plan — entitled to a Featured listing`
                                : "Free-tier submission — approve as Standard"
                            }
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                              marginTop: "0.3rem",
                              padding: "0.15rem 0.5rem",
                              borderRadius: "10px",
                              fontSize: "0.68rem",
                              fontWeight: 800,
                              letterSpacing: "0.04em",
                              background: item.ownerHasPaidPlan
                                ? (darkMode ? "rgba(184,134,43,0.22)" : "#fdf5e3")
                                : (darkMode ? "rgba(255,255,255,0.07)" : "#f1f5f9"),
                              color: item.ownerHasPaidPlan
                                ? (darkMode ? "#e3cb92" : "#8a6516")
                                : (darkMode ? "#94a3b8" : "#64748b"),
                              border: `1px solid ${
                                item.ownerHasPaidPlan
                                  ? (darkMode ? "rgba(227,203,146,0.35)" : "#e3cb92")
                                  : (darkMode ? "rgba(255,255,255,0.1)" : "#e2e8f0")
                              }`,
                            }}
                          >
                            {item.ownerHasPaidPlan ? "PAID — FEATURED" : "FREE TIER"}
                          </div>
                          {item.featured && (
                            <div
                              style={{
                                marginTop: "0.25rem",
                                fontSize: "0.68rem",
                                fontWeight: 800,
                                color: darkMode ? "#e3cb92" : "#8a6516",
                              }}
                            >
                              FEATURED LISTING
                            </div>
                          )}
                        </td>
                        <td>
                          <span style={{
                            padding: "0.2rem 0.6rem",
                            borderRadius: "12px",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            background: item.role === "Attorney"
                              ? (darkMode ? "rgba(37,99,235,0.2)" : "#dbeafe")
                              : (darkMode ? "rgba(147,51,234,0.2)" : "#f3e8ff"),
                            color: item.role === "Attorney" ? "#2563eb" : "#9333ea",
                          }}>
                            {item.role || "N/A"}
                          </span>
                        </td>
                        <td>
                          {item.emailPublic ? (
                            <div style={{ fontSize: "0.85rem", color: darkMode ? "#cbd5e1" : "#334155" }}>
                              {item.emailPublic}
                              {item.publishEmail && (
                                <span style={{ fontSize: "0.7rem", color: "#16a34a", marginLeft: "4px" }}>(Public)</span>
                              )}
                              <div
                                title={
                                  item.emailVerified
                                    ? "Submitter confirmed this address"
                                    : "Awaiting the submitter's email confirmation"
                                }
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "3px",
                                  marginTop: "0.2rem",
                                  fontSize: "0.7rem",
                                  fontWeight: 700,
                                  color: item.emailVerified ? "#16a34a" : "#d97706",
                                }}
                              >
                                {item.emailVerified ? <Check size={11} strokeWidth={3} /> : null}
                                {item.emailVerified ? "EMAIL VERIFIED" : "EMAIL UNVERIFIED"}
                              </div>
                            </div>
                          ) : (
                            <span style={{ fontSize: "0.8rem", color: darkMode ? "#64748b" : "#94a3b8", fontStyle: "italic" }}>
                              Private
                            </span>
                          )}
                        </td>
                        <td>
                          {item.website ? (
                            <a
                              href={item.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={item.website}
                              style={{
                                fontSize: "0.85rem",
                                color: darkMode ? "#60a5fa" : "#2563eb",
                                textDecoration: "underline",
                                wordBreak: "break-all",
                                display: "inline-block",
                                maxWidth: "200px",
                              }}
                            >
                              {item.website.replace(/^https?:\/\//i, "").replace(/\/$/, "")}
                            </a>
                          ) : (
                            <span style={{ fontSize: "0.8rem", color: darkMode ? "#64748b" : "#94a3b8", fontStyle: "italic" }}>
                              None
                            </span>
                          )}
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: "4px", flexWrap: "wrap", maxWidth: "220px" }}>
                            {(item.practiceAreas || []).map((pa) => (
                              <span key={pa} style={{
                                padding: "0.15rem 0.5rem",
                                borderRadius: "4px",
                                fontSize: "0.75rem",
                                background: darkMode ? "rgba(255,255,255,0.06)" : "#f1f5f9",
                                color: darkMode ? "#cbd5e1" : "#475569",
                              }}>
                                {pa === "Other" && item.practiceAreaOther ? item.practiceAreaOther : pa}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td>
                          <span style={{
                            display: "inline-block",
                            // "Directory Page" was wrapping inside the pill,
                            // so every row broke at a different place and the
                            // column read as ragged.
                            whiteSpace: "nowrap",
                            padding: "0.2rem 0.6rem",
                            borderRadius: "12px",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            background: item.source === "debate"
                              ? (darkMode ? "rgba(245,158,11,0.2)" : "#fef3c7")
                              : item.source === "directory"
                                ? (darkMode ? "rgba(139,92,246,0.2)" : "#ede9fe")
                                : (darkMode ? "rgba(59,130,246,0.2)" : "#e0f2fe"),
                            color: item.source === "debate"
                              ? "#d97706"
                              : item.source === "directory"
                                ? "#7c3aed"
                                : "#0284c7",
                          }}>
                            {item.source === "debate"
                              ? "Debate Mode"
                              : item.source === "directory"
                                ? "Directory Page"
                                : "Research Mode"}
                          </span>
                        </td>
                        <td style={{ fontSize: "0.8rem", color: darkMode ? "#94a3b8" : "#64748b" }}>
                          {new Date(item.createdAt).toLocaleDateString()}
                        </td>
                        <td>
                          <span style={{
                            padding: "0.25rem 0.65rem",
                            borderRadius: "12px",
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            background: item.status === "approved"
                              ? (darkMode ? "rgba(34,197,94,0.15)" : "#dcfce7")
                              : item.status === "rejected"
                              ? (darkMode ? "rgba(239,68,68,0.15)" : "#fee2e2")
                              : (darkMode ? "rgba(245,158,11,0.15)" : "#fef3c7"),
                            color: item.status === "approved"
                              ? "#16a34a"
                              : item.status === "rejected"
                              ? "#dc2626"
                              : "#d97706",
                          }}>
                            {item.status ? item.status.toUpperCase() : "APPROVED"}
                          </span>
                          {item.verified && (
                            <div
                              title="Listing verified"
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "3px",
                                marginTop: "0.25rem",
                                fontSize: "0.7rem",
                                fontWeight: 700,
                                color: "#16a34a",
                              }}
                            >
                              <Check size={12} strokeWidth={3} />
                              VERIFIED
                            </div>
                          )}
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap" }}>
                            <button
                              type="button"
                              title="Edit Listing"
                              onClick={() => handleEditDirListingClick(item)}
                              style={{
                                padding: "0.3rem 0.5rem",
                                borderRadius: "6px",
                                border: "none",
                                background: darkMode ? "rgba(59,130,246,0.2)" : "#e0f2fe",
                                color: "#0284c7",
                                cursor: "pointer",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                              }}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              title={item.verified ? "Remove verified mark" : "Mark listing as verified"}
                              onClick={() => handleToggleDirVerified(item)}
                              style={{
                                padding: "0.3rem 0.5rem",
                                borderRadius: "6px",
                                border: "none",
                                background: item.verified
                                  ? (darkMode ? "rgba(148,163,184,0.2)" : "#e2e8f0")
                                  : (darkMode ? "rgba(34,197,94,0.2)" : "#dcfce7"),
                                color: item.verified ? (darkMode ? "#cbd5e1" : "#475569") : "#16a34a",
                                cursor: "pointer",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                              }}
                            >
                              {item.verified ? "Unverify" : "Verify"}
                            </button>
                            {item.status !== "approved" && (
                              <button
                                type="button"
                                title="Approve Listing"
                                onClick={() => handleUpdateDirStatus(item._id, "approved")}
                                style={{
                                  padding: "0.3rem 0.5rem",
                                  borderRadius: "6px",
                                  border: "none",
                                  background: darkMode ? "rgba(34,197,94,0.2)" : "#dcfce7",
                                  color: "#16a34a",
                                  cursor: "pointer",
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                }}
                              >
                                Approve
                              </button>
                            )}
                            {item.status !== "rejected" && (
                              <button
                                type="button"
                                title="Reject Listing"
                                onClick={() => handleUpdateDirStatus(item._id, "rejected")}
                                style={{
                                  padding: "0.3rem 0.5rem",
                                  borderRadius: "6px",
                                  border: "none",
                                  background: darkMode ? "rgba(239,68,68,0.2)" : "#fee2e2",
                                  color: "#dc2626",
                                  cursor: "pointer",
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                }}
                              >
                                Reject
                              </button>
                            )}
                            <button
                              type="button"
                              title="Delete Listing"
                              onClick={() => handleDeleteDirListingClick(item)}
                              style={{
                                padding: "0.3rem 0.5rem",
                                borderRadius: "6px",
                                border: "none",
                                background: darkMode ? "rgba(100,116,139,0.2)" : "#f1f5f9",
                                color: darkMode ? "#94a3b8" : "#64748b",
                                cursor: "pointer",
                              }}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination Controls */}
            {!dirLoading && dirListings.length > 0 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginTop: "1.25rem",
                  padding: "0.75rem 1rem",
                  background: darkMode
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(0, 0, 0, 0.02)",
                  borderRadius: "0.5rem",
                  border: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0"}`,
                  flexWrap: "wrap",
                  gap: "1rem",
                }}
              >
                <div
                  style={{
                    fontSize: "0.875rem",
                    color: darkMode ? "#cbd5e1" : "#374151",
                  }}
                >
                  Showing {dirTotal === 0 ? 0 : (dirPage - 1) * dirLimit + 1} to{" "}
                  {Math.min(dirPage * dirLimit, dirTotal)} of {dirTotal} submissions
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "1rem",
                    flexWrap: "wrap",
                  }}
                >
                  {/* Page Size Selector */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.25rem",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "0.875rem",
                        color: darkMode ? "#cbd5e1" : "#374151",
                        marginRight: "0.25rem",
                      }}
                    >
                      Rows per page:
                    </span>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "0.125rem",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          const newLimit = Math.min(100, dirLimit + 5);
                          setDirLimit(newLimit);
                          setDirPage(1);
                        }}
                        disabled={dirLimit >= 100}
                        style={{
                          padding: "0.125rem 0.25rem",
                          border: `1px solid ${darkMode ? "#444" : "#ddd"}`,
                          borderRadius: "0.25rem 0.25rem 0 0",
                          background: darkMode ? "#2a2a2a" : "#fff",
                          color: darkMode ? "#fff" : "#333",
                          cursor: dirLimit >= 100 ? "not-allowed" : "pointer",
                          opacity: dirLimit >= 100 ? 0.5 : 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        title="Increase page size"
                      >
                        <ChevronUp size={14} />
                      </button>
                      <input
                        type="number"
                        min="5"
                        max="100"
                        value={dirLimitInput}
                        onChange={(e) => setDirLimitInput(e.target.value)}
                        onBlur={(e) => {
                          const value = e.target.value;
                          const numValue = parseInt(value, 10);
                          if (isNaN(numValue) || numValue < 5) {
                            setDirLimit(5);
                            setDirLimitInput("5");
                          } else if (numValue > 100) {
                            setDirLimit(100);
                            setDirLimitInput("100");
                          } else {
                            setDirLimit(numValue);
                          }
                          setDirPage(1);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.target.blur();
                          }
                        }}
                        style={{
                          padding: "0.25rem 0.5rem",
                          border: `1px solid ${darkMode ? "#444" : "#ddd"}`,
                          borderLeft: "none",
                          borderRight: "none",
                          background: darkMode ? "#1a1a1a" : "#f9f9f9",
                          color: darkMode ? "#fff" : "#333",
                          fontSize: "0.875rem",
                          fontWeight: "600",
                          minWidth: "3rem",
                          width: "3.5rem",
                          textAlign: "center",
                          outline: "none",
                          WebkitAppearance: "none",
                          MozAppearance: "textfield",
                        }}
                        title="Enter custom page size (5-100)"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newLimit = Math.max(5, dirLimit - 5);
                          setDirLimit(newLimit);
                          setDirPage(1);
                        }}
                        disabled={dirLimit <= 5}
                        style={{
                          padding: "0.125rem 0.25rem",
                          border: `1px solid ${darkMode ? "#444" : "#ddd"}`,
                          borderRadius: "0 0 0.25rem 0.25rem",
                          background: darkMode ? "#2a2a2a" : "#fff",
                          color: darkMode ? "#fff" : "#333",
                          cursor: dirLimit <= 5 ? "not-allowed" : "pointer",
                          opacity: dirLimit <= 5 ? 0.5 : 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        title="Decrease page size"
                      >
                        <ChevronDown size={14} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Navigation Buttons */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setDirPage(1)}
                    disabled={dirPage <= 1}
                    style={{
                      padding: "0.375rem 0.75rem",
                      fontSize: "0.875rem",
                      fontWeight: "600",
                      border: `1px solid ${darkMode ? "#444" : "#ddd"}`,
                      borderRadius: "0.375rem",
                      background: darkMode ? "#2a2a2a" : "#fff",
                      color: darkMode ? "#fff" : "#333",
                      cursor: dirPage <= 1 ? "not-allowed" : "pointer",
                      opacity: dirPage <= 1 ? 1 : 0.5,
                    }}
                  >
                    {"<<"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setDirPage(Math.max(1, dirPage - 1))}
                    disabled={dirPage <= 1}
                    style={{
                      padding: "0.375rem 0.75rem",
                      fontSize: "0.875rem",
                      fontWeight: "600",
                      border: `1px solid ${darkMode ? "#444" : "#ddd"}`,
                      borderRadius: "0.375rem",
                      background: darkMode ? "#2a2a2a" : "#fff",
                      color: darkMode ? "#fff" : "#333",
                      cursor: dirPage <= 1 ? "not-allowed" : "pointer",
                      opacity: dirPage <= 1 ? 1 : 0.5,
                    }}
                  >
                    {"<"}
                  </button>
                  <span
                    style={{
                      fontSize: "0.875rem",
                      color: darkMode ? "#cbd5e1" : "#374151",
                      padding: "0 0.5rem",
                    }}
                  >
                    Page {dirPage} of {dirTotalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setDirPage(Math.min(dirTotalPages, dirPage + 1))}
                    disabled={dirPage >= dirTotalPages}
                    style={{
                      padding: "0.375rem 0.75rem",
                      fontSize: "0.875rem",
                      fontWeight: "600",
                      border: `1px solid ${darkMode ? "#444" : "#ddd"}`,
                      borderRadius: "0.375rem",
                      background: darkMode ? "#2a2a2a" : "#fff",
                      color: darkMode ? "#fff" : "#333",
                      cursor: dirPage >= dirTotalPages ? "not-allowed" : "pointer",
                      opacity: dirPage >= dirTotalPages ? 1 : 0.5,
                    }}
                  >
                    {">"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setDirPage(dirTotalPages)}
                    disabled={dirPage >= dirTotalPages}
                    style={{
                      padding: "0.375rem 0.75rem",
                      fontSize: "0.875rem",
                      fontWeight: "600",
                      border: `1px solid ${darkMode ? "#444" : "#ddd"}`,
                      borderRadius: "0.375rem",
                      background: darkMode ? "#2a2a2a" : "#fff",
                      color: darkMode ? "#fff" : "#333",
                      cursor: dirPage >= dirTotalPages ? "not-allowed" : "pointer",
                      opacity: dirPage >= dirTotalPages ? 1 : 0.5,
                    }}
                  >
                    {">>"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Stats Cards */}
        {adminMainTab === "users" && (
          <>
            <div className="admin-stats">
              <div className="admin-stat-card">
                <div className="admin-stat-icon">
                  <Users size={24} />
                </div>
                <div className="admin-stat-content">
                  <p className="admin-stat-label">Total Users</p>
                  <p className="admin-stat-value">
                    {formatNumber(stats.totalUsers)}
                  </p>
                </div>
              </div>
              <div className="admin-stat-card">
                <div className="admin-stat-icon">
                  <TrendingUp size={24} />
                </div>
                <div className="admin-stat-content">
                  <p className="admin-stat-label">Total AI Actions Used</p>
                  <p className="admin-stat-value">
                    {formatNumber(stats.totalAIActions)}
                  </p>
                </div>
              </div>
              <div className="admin-stat-card">
                <div className="admin-stat-icon">
                  <TrendingUp size={24} />
                </div>
                <div className="admin-stat-content">
                  <p className="admin-stat-label">Average AI Actions</p>
                  <p className="admin-stat-value">
                    {formatNumber(stats.avgAIActionsPerUser)}
                  </p>
                </div>
              </div>
            </div>

            {/* Users Table */}
            {loading ? (
              <div className="admin-loading">
                <Loader className="spin" size={32} />
                <p>Loading users...</p>
              </div>
            ) : (
              <div className="admin-table-container">
                <div className="admin-table-controls">
                  <input
                    type="text"
                    className="admin-search-input"
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <select
                    className="admin-filter-select"
                    value={adminFilter}
                    onChange={(e) => setAdminFilter(e.target.value)}
                  >
                    <option value="all">All roles</option>
                    <option value="admin">Admins only</option>
                    <option value="user">Users only</option>
                  </select>
                  <select
                    className="admin-filter-select"
                    value={providerFilter}
                    onChange={(e) => setProviderFilter(e.target.value)}
                  >
                    <option value="all">All providers</option>
                    <option value="local">Email/password</option>
                    <option value="google">Google</option>
                    <option value="other">Other providers</option>
                  </select>
                </div>
                <table className="admin-table">
                  <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => {
                          const canSort = header.column.getCanSort();
                          const sortDirection = header.column.getIsSorted();
                          return (
                            <th
                              key={header.id}
                              onClick={
                                canSort
                                  ? header.column.getToggleSortingHandler()
                                  : undefined
                              }
                              className={canSort ? "admin-th-sortable" : ""}
                              style={{
                                cursor: canSort ? "pointer" : "default",
                                textAlign: "center",
                              }}
                            >
                              {header.isPlaceholder ? null : (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "0.25rem",
                                  }}
                                >
                                  {flexRender(
                                    header.column.columnDef.header,
                                    header.getContext(),
                                  )}
                                  {canSort && (
                                    <span>
                                      {sortDirection === "asc"
                                        ? " ▲"
                                        : sortDirection === "desc"
                                          ? " ▼"
                                          : " ↕"}
                                    </span>
                                  )}
                                </div>
                              )}
                            </th>
                          );
                        })}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={columns.length}
                          className="admin-empty"
                          style={{ textAlign: "center" }}
                        >
                          No users found
                        </td>
                      </tr>
                    ) : (
                      table.getRowModel().rows.map((row) => {
                        const user = row.original;
                        return (
                          <tr
                            key={row.id}
                            onClick={() =>
                              navigate(`/admin/user-actions/${user.userId}`)
                            }
                            style={{
                              cursor: "pointer",
                            }}
                            className="admin-table-row-clickable"
                          >
                            {row.getVisibleCells().map((cell) => {
                              const isActionsColumn = cell.column.id === "actions";
                              return (
                                <td
                                  key={cell.id}
                                  style={{ textAlign: "center" }}
                                  onClick={(e) => {
                                    // Stop propagation for Actions column and interactive elements
                                    if (isActionsColumn) {
                                      e.stopPropagation();
                                      return;
                                    }
                                    const target = e.target;
                                    if (
                                      target.tagName === "BUTTON" ||
                                      target.closest("button") ||
                                      target.closest(".admin-menu-trigger") ||
                                      target.closest(".admin-actions-menu") ||
                                      target.closest("a")
                                    ) {
                                      e.stopPropagation();
                                    }
                                  }}
                                >
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext(),
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
                {/* Pagination Controls */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginTop: "1rem",
                    padding: "0.75rem",
                    background: darkMode
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(0, 0, 0, 0.02)",
                    borderRadius: "0.5rem",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.875rem",
                      color: darkMode ? "#cbd5e1" : "#374151",
                    }}
                  >
                    Showing{" "}
                    {table.getState().pagination.pageIndex *
                      table.getState().pagination.pageSize +
                      1}{" "}
                    to{" "}
                    {Math.min(
                      (table.getState().pagination.pageIndex + 1) *
                      table.getState().pagination.pageSize,
                      table.getFilteredRowModel().rows.length,
                    )}{" "}
                    of {table.getFilteredRowModel().rows.length} users
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "1rem",
                    }}
                  >
                    {/* Page Size Selector */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.25rem",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.875rem",
                          color: darkMode ? "#cbd5e1" : "#374151",
                          marginRight: "0.25rem",
                        }}
                      >
                        Rows per page:
                      </span>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "0.125rem",
                        }}
                      >
                        <button
                          onClick={() => {
                            const newPageSize = Math.min(
                              100,
                              table.getState().pagination.pageSize + 5,
                            );
                            table.setPageSize(newPageSize);
                            table.setPageIndex(0);
                            setPageSizeInput(String(newPageSize));
                          }}
                          disabled={table.getState().pagination.pageSize >= 100}
                          style={{
                            padding: "0.125rem 0.25rem",
                            border: `1px solid ${darkMode ? "#444" : "#ddd"}`,
                            borderRadius: "0.25rem 0.25rem 0 0",
                            background: darkMode ? "#2a2a2a" : "#fff",
                            color: darkMode ? "#fff" : "#333",
                            cursor:
                              table.getState().pagination.pageSize >= 100
                                ? "not-allowed"
                                : "pointer",
                            opacity:
                              table.getState().pagination.pageSize >= 100 ? 0.5 : 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                          title="Increase page size"
                        >
                          <ChevronUp size={14} />
                        </button>
                        <input
                          type="number"
                          min="5"
                          max="100"
                          value={pageSizeInput}
                          onChange={(e) => {
                            setPageSizeInput(e.target.value);
                          }}
                          onBlur={(e) => {
                            const value = e.target.value;
                            const numValue = parseInt(value, 10);
                            if (isNaN(numValue) || numValue < 5) {
                              table.setPageSize(5);
                              table.setPageIndex(0);
                            } else if (numValue > 100) {
                              table.setPageSize(100);
                              table.setPageIndex(0);
                            } else {
                              table.setPageSize(numValue);
                              table.setPageIndex(0);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.target.blur();
                            }
                          }}
                          style={{
                            padding: "0.25rem 0.5rem",
                            border: `1px solid ${darkMode ? "#444" : "#ddd"}`,
                            borderLeft: "none",
                            borderRight: "none",
                            background: darkMode ? "#1a1a1a" : "#f9f9f9",
                            color: darkMode ? "#fff" : "#333",
                            fontSize: "0.875rem",
                            fontWeight: "600",
                            minWidth: "3rem",
                            width: "3.5rem",
                            textAlign: "center",
                            outline: "none",
                            WebkitAppearance: "none",
                            MozAppearance: "textfield",
                          }}
                          title="Enter custom page size (5-100)"
                        />
                        <button
                          onClick={() => {
                            const newPageSize = Math.max(
                              5,
                              table.getState().pagination.pageSize - 5,
                            );
                            table.setPageSize(newPageSize);
                            table.setPageIndex(0);
                            setPageSizeInput(String(newPageSize));
                          }}
                          disabled={table.getState().pagination.pageSize <= 5}
                          style={{
                            padding: "0.125rem 0.25rem",
                            border: `1px solid ${darkMode ? "#444" : "#ddd"}`,
                            borderRadius: "0 0 0.25rem 0.25rem",
                            background: darkMode ? "#2a2a2a" : "#fff",
                            color: darkMode ? "#fff" : "#333",
                            cursor:
                              table.getState().pagination.pageSize <= 5
                                ? "not-allowed"
                                : "pointer",
                            opacity:
                              table.getState().pagination.pageSize <= 5 ? 0.5 : 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                          title="Decrease page size"
                        >
                          <ChevronDown size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <button
                      onClick={() => table.setPageIndex(0)}
                      disabled={!table.getCanPreviousPage()}
                      style={{
                        padding: "0.375rem 0.75rem",
                        fontSize: "0.875rem",
                        fontWeight: "600",
                        border: `1px solid ${darkMode ? "#444" : "#ddd"}`,
                        borderRadius: "0.375rem",
                        background: darkMode ? "#2a2a2a" : "#fff",
                        color: darkMode ? "#fff" : "#333",
                        cursor: table.getCanPreviousPage()
                          ? "pointer"
                          : "not-allowed",
                        opacity: table.getCanPreviousPage() ? 1 : 0.5,
                      }}
                    >
                      {"<<"}
                    </button>
                    <button
                      onClick={() => table.previousPage()}
                      disabled={!table.getCanPreviousPage()}
                      style={{
                        padding: "0.375rem 0.75rem",
                        fontSize: "0.875rem",
                        fontWeight: "600",
                        border: `1px solid ${darkMode ? "#444" : "#ddd"}`,
                        borderRadius: "0.375rem",
                        background: darkMode ? "#2a2a2a" : "#fff",
                        color: darkMode ? "#fff" : "#333",
                        cursor: table.getCanPreviousPage()
                          ? "pointer"
                          : "not-allowed",
                        opacity: table.getCanPreviousPage() ? 1 : 0.5,
                      }}
                    >
                      {"<"}
                    </button>
                    <span
                      style={{
                        fontSize: "0.875rem",
                        color: darkMode ? "#cbd5e1" : "#374151",
                        padding: "0 0.5rem",
                      }}
                    >
                      Page {table.getState().pagination.pageIndex + 1} of{" "}
                      {table.getPageCount()}
                    </span>
                    <button
                      onClick={() => table.nextPage()}
                      disabled={!table.getCanNextPage()}
                      style={{
                        padding: "0.375rem 0.75rem",
                        fontSize: "0.875rem",
                        fontWeight: "600",
                        border: `1px solid ${darkMode ? "#444" : "#ddd"}`,
                        borderRadius: "0.375rem",
                        background: darkMode ? "#2a2a2a" : "#fff",
                        color: darkMode ? "#fff" : "#333",
                        cursor: table.getCanNextPage() ? "pointer" : "not-allowed",
                        opacity: table.getCanNextPage() ? 1 : 0.5,
                      }}
                    >
                      {">"}
                    </button>
                    <button
                      onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                      disabled={!table.getCanNextPage()}
                      style={{
                        padding: "0.375rem 0.75rem",
                        fontSize: "0.875rem",
                        fontWeight: "600",
                        border: `1px solid ${darkMode ? "#444" : "#ddd"}`,
                        borderRadius: "0.375rem",
                        background: darkMode ? "#2a2a2a" : "#fff",
                        color: darkMode ? "#fff" : "#333",
                        cursor: table.getCanNextPage() ? "pointer" : "not-allowed",
                        opacity: table.getCanNextPage() ? 1 : 0.5,
                      }}
                    >
                      {">>"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          isOpen={deleteDialog.isOpen}
          title="Delete User"
          message={
            deleteDialog.user
              ? `Are you sure you want to delete user "${deleteDialog.user.name}" (${deleteDialog.user.email})? This action cannot be undone and will permanently remove all user data.`
              : ""
          }
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
        />

        {/* Admin Status Confirmation Dialog */}
        <ConfirmDialog
          isOpen={adminStatusDialog.isOpen}
          title={
            adminStatusDialog.newStatus
              ? "Grant Admin Access"
              : "Revoke Admin Access"
          }
          message={
            adminStatusDialog.user
              ? `Are you sure you want to ${adminStatusDialog.newStatus ? "grant" : "revoke"} admin access for "${adminStatusDialog.user.name}" (${adminStatusDialog.user.email})?`
              : ""
          }
          confirmText={
            adminStatusDialog.newStatus ? "Grant Admin" : "Revoke Admin"
          }
          cancelText="Cancel"
          type={adminStatusDialog.newStatus ? "default" : "warning"}
          onConfirm={handleAdminStatusConfirm}
          onCancel={handleAdminStatusCancel}
        />

        {/* User Status Confirmation Dialog */}
        <ConfirmDialog
          isOpen={userStatusDialog.isOpen}
          title={userStatusDialog.newStatus ? "Activate User" : "Deactivate User"}
          message={
            userStatusDialog.user
              ? `Are you sure you want to ${userStatusDialog.newStatus ? "activate" : "deactivate"} user "${userStatusDialog.user.name}" (${userStatusDialog.user.email})?`
              : ""
          }
          confirmText={userStatusDialog.newStatus ? "Activate" : "Deactivate"}
          cancelText="Cancel"
          type={userStatusDialog.newStatus ? "default" : "warning"}
          onConfirm={handleUserStatusConfirm}
          onCancel={handleUserStatusCancel}
        />

        {/* Add Tokens Dialog */}
        {addTokensDialog.isOpen && addTokensDialog.user && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
            onClick={handleAddTokensCancel}
          >
            <div
              style={{
                background: darkMode ? "#2a2a2a" : "#fff",
                borderRadius: "0.5rem",
                padding: "1.5rem",
                minWidth: "400px",
                maxWidth: "500px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onMouseUp={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <h3
                style={{
                  margin: "0 0 1rem 0",
                  fontSize: "1.25rem",
                  fontWeight: "700",
                  color: darkMode ? "#fff" : "#333",
                }}
              >
                Add AI Actions to User
              </h3>
              <p
                style={{
                  margin: "0 0 1rem 0",
                  fontSize: "0.875rem",
                  color: darkMode ? "#a0a0a0" : "#666",
                }}
              >
                User: <strong>{addTokensDialog.user.name}</strong> (
                {addTokensDialog.user.email})
              </p>
              <p
                style={{
                  margin: "0 0 1rem 0",
                  fontSize: "0.875rem",
                  color: darkMode ? "#a0a0a0" : "#666",
                }}
              >
                Current AI Actions used:{" "}
                <strong>
                  {formatNumber(addTokensDialog.user.subscriptionStatus === "trialing" ? (addTokensDialog.user.freeTrialTokensUsed || 0) : (addTokensDialog.user.aiActionsUsed || 0))}
                </strong>
              </p>
              <p
                style={{
                  margin: "0 0 1rem 0",
                  fontSize: "0.875rem",
                  color: darkMode ? "#a0a0a0" : "#666",
                }}
              >
                Current AI Actions limit:{" "}
                <strong>
                  {formatNumber(addTokensDialog.user.aiMonthlyLimit || 500)}
                </strong>
              </p>
              <div style={{ marginBottom: "1rem" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontSize: "0.875rem",
                    fontWeight: "600",
                    color: darkMode ? "#fff" : "#333",
                  }}
                >
                  AI Actions to Add:
                </label>
                <input
                  type="number"
                  min="1"
                  value={addTokensDialog.tokensToAdd}
                  onChange={(e) =>
                    setAddTokensDialog({
                      ...addTokensDialog,
                      tokensToAdd: e.target.value,
                    })
                  }
                  placeholder="Enter number of actions"
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    border: `1px solid ${darkMode ? "#444" : "#ddd"}`,
                    borderRadius: "0.375rem",
                    fontSize: "0.875rem",
                    background: darkMode ? "#1a1a1a" : "#fff",
                    color: darkMode ? "#fff" : "#333",
                  }}
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleAddTokensConfirm();
                    }
                  }}
                />
                <p
                  style={{
                    margin: "0.5rem 0 0 0",
                    fontSize: "0.75rem",
                    color: darkMode ? "#888" : "#999",
                  }}
                >
                  This will increase the user's monthly AI actions limit.
                </p>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "0.75rem",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  onClick={handleAddTokensCancel}
                  style={{
                    padding: "0.75rem 1.5rem",
                    border: `1px solid ${darkMode ? "#444" : "#ddd"}`,
                    borderRadius: "0.375rem",
                    background: "none",
                    color: darkMode ? "#fff" : "#333",
                    cursor: "pointer",
                    fontSize: "0.875rem",
                    fontWeight: "600",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddTokensConfirm}
                  disabled={
                    addingTokens.has(addTokensDialog.user.userId) ||
                    !addTokensDialog.tokensToAdd
                  }
                  style={{
                    padding: "0.75rem 1.5rem",
                    border: "none",
                    borderRadius: "0.375rem",
                    background: addingTokens.has(addTokensDialog.user.userId)
                      ? "#999"
                      : "#22c55e",
                    color: "#fff",
                    cursor: addingTokens.has(addTokensDialog.user.userId)
                      ? "not-allowed"
                      : "pointer",
                    fontSize: "0.875rem",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  {addingTokens.has(addTokensDialog.user.userId) ? (
                    <>
                      <Loader className="spin" size={16} />
                      Adding...
                    </>
                  ) : (
                    "Add AI Actions"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Invite User Dialog */}
        {inviteUserDialog.isOpen && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
          >
            <div
              style={{
                background: darkMode ? "#2a2a2a" : "#fff",
                borderRadius: "0.5rem",
                padding: "1.5rem",
                minWidth: "400px",
                maxWidth: "500px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3
                style={{
                  margin: "0 0 1rem 0",
                  fontSize: "1.25rem",
                  fontWeight: "700",
                  color: darkMode ? "#fff" : "#333",
                }}
              >
                Invite User
              </h3>
              <p
                style={{
                  margin: "0 0 1rem 0",
                  fontSize: "0.875rem",
                  color: darkMode ? "#a0a0a0" : "#666",
                }}
              >
                Enter the email address to send a user invitation. The user will
                receive a link to create their account.
              </p>
              <div style={{ marginBottom: "1rem" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontSize: "0.875rem",
                    fontWeight: "600",
                    color: darkMode ? "#fff" : "#333",
                  }}
                >
                  Email Address:
                </label>
                <input
                  type="email"
                  value={inviteUserDialog.email}
                  onChange={(e) =>
                    setInviteUserDialog({
                      ...inviteUserDialog,
                      email: e.target.value,
                    })
                  }
                  placeholder="Enter email address"
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    border: `1px solid ${darkMode ? "#444" : "#ddd"}`,
                    borderRadius: "0.375rem",
                    fontSize: "0.875rem",
                    background: darkMode ? "#1a1a1a" : "#fff",
                    color: darkMode ? "#fff" : "#333",
                  }}
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleInviteUserConfirm();
                    }
                  }}
                />
              </div>
              <div style={{ marginBottom: "1rem" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontSize: "0.875rem",
                    fontWeight: "600",
                    color: darkMode ? "#fff" : "#333",
                  }}
                >
                  Initial AI Action Limit:
                </label>
                <input
                  type="number"
                  value={inviteUserDialog.initialAiActionLimit}
                  onChange={(e) =>
                    setInviteUserDialog({
                      ...inviteUserDialog,
                      initialAiActionLimit:
                        e.target.value === "" ? "" : parseInt(e.target.value) || 0,
                    })
                  }
                  placeholder="1-800"
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    border: `1px solid ${darkMode ? "#444" : "#ddd"}`,
                    borderRadius: "0.375rem",
                    fontSize: "0.875rem",
                    background: darkMode ? "#1a1a1a" : "#fff",
                    color: darkMode ? "#fff" : "#333",
                  }}
                />
                <p
                  style={{
                    margin: "0.5rem 0 0 0",
                    fontSize: "0.75rem",
                    color: darkMode ? "#888" : "#999",
                  }}
                >
                  Set the initial number of AI actions the user can perform
                  (between 1 and 800).
                </p>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "0.75rem",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  onClick={handleInviteUserCancel}
                  style={{
                    padding: "0.75rem 1.5rem",
                    border: `1px solid ${darkMode ? "#444" : "#ddd"}`,
                    borderRadius: "0.375rem",
                    background: "none",
                    color: darkMode ? "#fff" : "#333",
                    cursor: "pointer",
                    fontSize: "0.875rem",
                    fontWeight: "600",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleInviteUserConfirm}
                  disabled={sendingInvite || !inviteUserDialog.email.trim()}
                  style={{
                    padding: "0.75rem 1.5rem",
                    border: "none",
                    borderRadius: "0.375rem",
                    background: sendingInvite ? "#999" : "#2563eb",
                    color: "#fff",
                    cursor: sendingInvite ? "not-allowed" : "pointer",
                    fontSize: "0.875rem",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  {sendingInvite ? (
                    <>
                      <Loader className="spin" size={16} />
                      Sending...
                    </>
                  ) : (
                    "Send Invitation"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Directory Listing Dialog */}
        {dirEdit && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              padding: "1rem",
            }}
            {...overlayCloseProps(() => !dirEditSaving && setDirEdit(null))}
          >
            <div
              style={{
                background: darkMode ? "#2a2a2a" : "#fff",
                borderRadius: "0.5rem",
                padding: "1.5rem",
                width: "100%",
                maxWidth: "620px",
                maxHeight: "88vh",
                overflowY: "auto",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3
                style={{
                  margin: "0 0 0.35rem 0",
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  color: darkMode ? "#fff" : "#333",
                }}
              >
                Edit Directory Listing
              </h3>
              <p
                style={{
                  margin: "0 0 1.25rem 0",
                  fontSize: "0.8rem",
                  color: darkMode ? "#a0a0a0" : "#666",
                }}
              >
                Correct spelling, a mistyped URL, or any other detail on the submitter's behalf.
              </p>

              {dirEditError && (
                <p
                  style={{
                    margin: "0 0 1rem 0",
                    padding: "0.6rem 0.75rem",
                    borderRadius: "6px",
                    background: darkMode ? "rgba(239,68,68,0.15)" : "#fee2e2",
                    color: "#dc2626",
                    fontSize: "0.8rem",
                    fontWeight: 600,
                  }}
                >
                  {dirEditError}
                </p>
              )}

              <div
                style={{
                  display: "grid",
                  gap: "4px",
                  marginBottom: "0.75rem",
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  color: darkMode ? "#cbd5e1" : "#475569",
                }}
              >
                <span>
                  Verification Email{" "}
                  <span style={{ fontWeight: 500, color: darkMode ? "#94a3b8" : "#64748b" }}>
                    (private — admin only)
                  </span>
                </span>
                {dirEdit.verificationEmail ? (
                  <a
                    href={`mailto:${dirEdit.verificationEmail}`}
                    style={{
                      padding: "0.5rem 0.65rem",
                      borderRadius: "6px",
                      border: `1px dashed ${darkMode ? "rgba(255,255,255,0.2)" : "#cbd5e1"}`,
                      background: darkMode ? "rgba(255,255,255,0.03)" : "#f8fafc",
                      color: "#2563eb",
                      fontSize: "0.85rem",
                      fontWeight: 500,
                      textDecoration: "none",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {dirEdit.verificationEmail}
                  </a>
                ) : (
                  <span
                    style={{
                      padding: "0.5rem 0.65rem",
                      borderRadius: "6px",
                      border: `1px dashed ${darkMode ? "rgba(255,255,255,0.2)" : "#cbd5e1"}`,
                      color: darkMode ? "#94a3b8" : "#64748b",
                      fontSize: "0.85rem",
                      fontWeight: 500,
                      fontStyle: "italic",
                    }}
                  >
                    Not on file (listing predates private email storage)
                  </span>
                )}
                <span style={{ fontSize: "0.72rem", fontWeight: 500, color: darkMode ? "#94a3b8" : "#64748b" }}>
                  Never shown on the public listing. Only the Public Contact Email below is published, and only when "Publish email" is on.
                </span>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
                {[
                  { field: "firstName", label: "First Name" },
                  { field: "lastName", label: "Last Name" },
                  { field: "firmName", label: "Firm / Organization" },
                  { field: "yearsInPractice", label: "Years in Practice" },
                  { field: "website", label: "Professional Website" },
                  { field: "city", label: "City" },
                  { field: "state", label: "State" },
                  { field: "emailPublic", label: "Public Contact Email" },
                  { field: "phone", label: "Phone (private)" },
                  { field: "phonePublic", label: "Phone (shown on listing)" },
                ].map(({ field, label }) => (
                  <label key={field} style={{ display: "grid", gap: "4px", fontSize: "0.78rem", fontWeight: 600, color: darkMode ? "#cbd5e1" : "#475569" }}>
                    {label}
                    <input
                      type="text"
                      value={dirEdit[field]}
                      onChange={(e) => handleDirEditField(field, e.target.value)}
                      style={{
                        padding: "0.5rem 0.65rem",
                        borderRadius: "6px",
                        border: `1px solid ${darkMode ? "rgba(255,255,255,0.15)" : "#cbd5e1"}`,
                        background: darkMode ? "#1e293b" : "#fff",
                        color: darkMode ? "#f8fafc" : "#0f172a",
                        fontSize: "0.85rem",
                        fontFamily: "inherit",
                      }}
                    />
                  </label>
                ))}

                <label style={{ display: "grid", gap: "4px", fontSize: "0.78rem", fontWeight: 600, color: darkMode ? "#cbd5e1" : "#475569" }}>
                  Professional Role
                  <select
                    value={dirEdit.role}
                    onChange={(e) => handleDirEditField("role", e.target.value)}
                    style={{
                      padding: "0.5rem 0.65rem",
                      borderRadius: "6px",
                      border: `1px solid ${darkMode ? "rgba(255,255,255,0.15)" : "#cbd5e1"}`,
                      background: darkMode ? "#1e293b" : "#fff",
                      color: darkMode ? "#f8fafc" : "#0f172a",
                      fontSize: "0.85rem",
                      fontFamily: "inherit",
                    }}
                  >
                    <option value="">Not set</option>
                    <option value="Attorney">Attorney</option>
                    <option value="Paralegal">Paralegal</option>
                  </select>
                </label>
              </div>

              <div style={{ marginTop: "1rem" }}>
                <p style={{ margin: "0 0 0.4rem", fontSize: "0.78rem", fontWeight: 600, color: darkMode ? "#cbd5e1" : "#475569" }}>
                  Practice Areas ({(dirEdit.practiceAreas || []).length} of 3)
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
                  {ADMIN_PRACTICE_AREAS.map((area) => {
                    const selected = (dirEdit.practiceAreas || []).includes(area);
                    return (
                      <button
                        key={area}
                        type="button"
                        onClick={() => handleDirEditPracticeArea(area)}
                        style={{
                          padding: "0.3rem 0.6rem",
                          borderRadius: "6px",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          cursor: "pointer",
                          border: `1px solid ${selected ? "#2563eb" : (darkMode ? "rgba(255,255,255,0.15)" : "#cbd5e1")}`,
                          background: selected
                            ? (darkMode ? "rgba(37,99,235,0.25)" : "#dbeafe")
                            : (darkMode ? "#1e293b" : "#fff"),
                          color: selected ? "#2563eb" : (darkMode ? "#cbd5e1" : "#475569"),
                        }}
                      >
                        {area}
                      </button>
                    );
                  })}
                </div>
              </div>

              {(dirEdit.practiceAreas || []).includes("Other") && (
                <label style={{ display: "grid", gap: "4px", marginTop: "0.75rem", fontSize: "0.78rem", fontWeight: 600, color: darkMode ? "#cbd5e1" : "#475569" }}>
                  Other Practice Area
                  <input
                    type="text"
                    value={dirEdit.practiceAreaOther}
                    onChange={(e) => handleDirEditField("practiceAreaOther", e.target.value)}
                    style={{
                      padding: "0.5rem 0.65rem",
                      borderRadius: "6px",
                      border: `1px solid ${darkMode ? "rgba(255,255,255,0.15)" : "#cbd5e1"}`,
                      background: darkMode ? "#1e293b" : "#fff",
                      color: darkMode ? "#f8fafc" : "#0f172a",
                      fontSize: "0.85rem",
                      fontFamily: "inherit",
                    }}
                  />
                </label>
              )}

              <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", marginTop: "1rem" }}>
                {[
                  { field: "verified", label: "Listing verified" },
                  { field: "emailVerified", label: "Email verified" },
                  { field: "freeConsultation", label: "Offers free consultation" },
                  { field: "publishEmail", label: "Publish email" },
                  { field: "publicList", label: "Show in public directory" },
                ].map(({ field, label }) => (
                  <label key={field} style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", fontSize: "0.8rem", fontWeight: 600, color: darkMode ? "#cbd5e1" : "#475569", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={!!dirEdit[field]}
                      onChange={(e) => handleDirEditField(field, e.target.checked)}
                    />
                    {label}
                  </label>
                ))}
              </div>

              <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end", marginTop: "1.5rem" }}>
                <button
                  type="button"
                  onClick={() => setDirEdit(null)}
                  disabled={dirEditSaving}
                  style={{
                    padding: "0.5rem 1rem",
                    borderRadius: "6px",
                    border: `1px solid ${darkMode ? "rgba(255,255,255,0.15)" : "#cbd5e1"}`,
                    background: "transparent",
                    color: darkMode ? "#cbd5e1" : "#475569",
                    cursor: dirEditSaving ? "not-allowed" : "pointer",
                    fontSize: "0.85rem",
                    fontWeight: 600,
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveDirListing}
                  disabled={dirEditSaving}
                  style={{
                    padding: "0.5rem 1rem",
                    borderRadius: "6px",
                    border: "none",
                    background: "#2563eb",
                    color: "#fff",
                    cursor: dirEditSaving ? "not-allowed" : "pointer",
                    opacity: dirEditSaving ? 0.7 : 1,
                    fontSize: "0.85rem",
                    fontWeight: 700,
                  }}
                >
                  {dirEditSaving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Directory Listing Confirmation Dialog */}
        {dirDeleteConfirm.isOpen && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
            {...overlayCloseProps(handleDeleteDirListingCancel)}
          >
            <div
              style={{
                background: darkMode ? "#2a2a2a" : "#fff",
                borderRadius: "0.5rem",
                padding: "1.5rem",
                minWidth: "400px",
                maxWidth: "500px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3
                style={{
                  margin: "0 0 1rem 0",
                  fontSize: "1.25rem",
                  fontWeight: "700",
                  color: darkMode ? "#fff" : "#333",
                }}
              >
                Delete Directory Listing
              </h3>
              <p
                style={{
                  margin: "0 0 1.5rem 0",
                  fontSize: "0.875rem",
                  color: darkMode ? "#a0a0a0" : "#666",
                }}
              >
                Are you sure you want to delete the listing for{" "}
                <strong style={{ color: darkMode ? "#fff" : "#333" }}>
                  {dirDeleteConfirm.name}
                </strong>
                ? This action cannot be undone.
              </p>
              <div
                style={{
                  display: "flex",
                  gap: "0.75rem",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  onClick={handleDeleteDirListingCancel}
                  disabled={dirDeleting}
                  style={{
                    padding: "0.75rem 1.5rem",
                    border: `1px solid ${darkMode ? "#444" : "#ddd"}`,
                    borderRadius: "0.375rem",
                    background: "none",
                    color: darkMode ? "#fff" : "#333",
                    cursor: dirDeleting ? "not-allowed" : "pointer",
                    fontSize: "0.875rem",
                    fontWeight: "600",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteDirListingConfirm}
                  disabled={dirDeleting}
                  style={{
                    padding: "0.75rem 1.5rem",
                    border: "none",
                    borderRadius: "0.375rem",
                    background: dirDeleting ? "#999" : "#dc2626",
                    color: "#fff",
                    cursor: dirDeleting ? "not-allowed" : "pointer",
                    fontSize: "0.875rem",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  {dirDeleting ? (
                    <>
                      <Loader className="spin" size={16} />
                      Deleting...
                    </>
                  ) : (
                    "Delete"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Invite Tester Dialog */}
        {inviteTesterDialog.isOpen && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
          >
            <div
              style={{
                background: darkMode ? "#2a2a2a" : "#fff",
                borderRadius: "0.5rem",
                padding: "1.5rem",
                minWidth: "400px",
                maxWidth: "500px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3
                style={{
                  margin: "0 0 1rem 0",
                  fontSize: "1.25rem",
                  fontWeight: "700",
                  color: darkMode ? "#fff" : "#333",
                }}
              >
                Invite Tester
              </h3>
              <p
                style={{
                  margin: "0 0 1rem 0",
                  fontSize: "0.875rem",
                  color: darkMode ? "#a0a0a0" : "#666",
                }}
              >
                Enter the email address to send a tester invitation. The tester
                will receive a set number of AI actions.
              </p>
              <div style={{ marginBottom: "1rem" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontSize: "0.875rem",
                    fontWeight: "600",
                    color: darkMode ? "#fff" : "#333",
                  }}
                >
                  Email Address:
                </label>
                <input
                  type="email"
                  value={inviteTesterDialog.email}
                  onChange={(e) =>
                    setInviteTesterDialog({
                      ...inviteTesterDialog,
                      email: e.target.value,
                    })
                  }
                  placeholder="Enter email address"
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    border: `1px solid ${darkMode ? "#444" : "#ddd"}`,
                    borderRadius: "0.375rem",
                    fontSize: "0.875rem",
                    background: darkMode ? "#1a1a1a" : "#fff",
                    color: darkMode ? "#fff" : "#333",
                  }}
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleInviteTesterConfirm();
                    }
                  }}
                />
              </div>
              <div style={{ marginBottom: "1rem" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontSize: "0.875rem",
                    fontWeight: "600",
                    color: darkMode ? "#fff" : "#333",
                  }}
                >
                  Initial AI Action Limit:
                </label>
                <input
                  type="number"
                  value={inviteTesterDialog.initialAiActionLimit}
                  onChange={(e) =>
                    setInviteTesterDialog({
                      ...inviteTesterDialog,
                      initialAiActionLimit:
                        e.target.value === "" ? "" : parseInt(e.target.value) || 0,
                    })
                  }
                  placeholder="1-800"
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    border: `1px solid ${darkMode ? "#444" : "#ddd"}`,
                    borderRadius: "0.375rem",
                    fontSize: "0.875rem",
                    background: darkMode ? "#1a1a1a" : "#fff",
                    color: darkMode ? "#fff" : "#333",
                  }}
                />
                <p
                  style={{
                    margin: "0.5rem 0 0 0",
                    fontSize: "0.75rem",
                    color: darkMode ? "#888" : "#999",
                  }}
                >
                  Set the initial number of AI actions the tester can perform
                  (between 1 and 800).
                </p>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "0.75rem",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  onClick={handleInviteTesterCancel}
                  style={{
                    padding: "0.75rem 1.5rem",
                    border: `1px solid ${darkMode ? "#444" : "#ddd"}`,
                    borderRadius: "0.375rem",
                    background: "none",
                    color: darkMode ? "#fff" : "#333",
                    cursor: "pointer",
                    fontSize: "0.875rem",
                    fontWeight: "600",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleInviteTesterConfirm}
                  disabled={sendingInvite || !inviteTesterDialog.email.trim()}
                  style={{
                    padding: "0.75rem 1.5rem",
                    border: "none",
                    borderRadius: "0.375rem",
                    background: sendingInvite ? "#999" : "#22c55e",
                    color: "#fff",
                    cursor: sendingInvite ? "not-allowed" : "pointer",
                    fontSize: "0.875rem",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  {sendingInvite ? (
                    <>
                      <Loader className="spin" size={16} />
                      Sending...
                    </>
                  ) : (
                    "Send Invitation"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {openMenuId &&
        actionsMenuLayout &&
        actionsMenuUser &&
        createPortal(
          <>
            <div
              aria-hidden="true"
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 9998,
              }}
              onClick={closeActionsMenu}
            />
            <div
              className="admin-actions-menu admin-actions-menu-portal"
              style={{
                position: "fixed",
                top: actionsMenuLayout.top,
                left: actionsMenuLayout.left,
                width: actionsMenuLayout.width,
                maxHeight: actionsMenuLayout.maxHeight,
                overflowY: "auto",
                background: darkMode ? "#2a2a2a" : "#fff",
                border: `1px solid ${darkMode ? "#444" : "#ddd"}`,
                borderRadius: "0.5rem",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                zIndex: 9999,
                minWidth: "200px",
                padding: "0.5rem 0",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className={`admin-menu-item ${actionsMenuUser.is_Admin === true ? "admin-menu-item-revoke" : "admin-menu-item-grant"}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setAdminStatusDialog({
                    isOpen: true,
                    user: actionsMenuUser,
                    newStatus: !(actionsMenuUser.is_Admin === true),
                  });
                  closeActionsMenu();
                }}
                disabled={
                  updatingUsers.has(actionsMenuUser.userId) ||
                  actionsMenuUser.isAnonymous
                }
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  padding: "0.75rem 1rem",
                  background: "none",
                  border: "none",
                  cursor:
                    updatingUsers.has(actionsMenuUser.userId) ||
                    actionsMenuUser.isAnonymous
                      ? "not-allowed"
                      : "pointer",
                  opacity: actionsMenuUser.isAnonymous ? 0.5 : 1,
                  color: darkMode ? "#fff" : "#333",
                  fontSize: "0.875rem",
                  textAlign: "left",
                  transition: "background 0.2s",
                }}
                onMouseEnter={(e) => {
                  if (
                    !updatingUsers.has(actionsMenuUser.userId) &&
                    !actionsMenuUser.isAnonymous
                  ) {
                    e.currentTarget.style.background = darkMode
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(0, 0, 0, 0.05)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "none";
                }}
              >
                {updatingUsers.has(actionsMenuUser.userId) ? (
                  <Loader className="spin" size={16} />
                ) : actionsMenuUser.is_Admin === true ? (
                  <>
                    <ShieldOff size={16} />
                    <span>Revoke Admin</span>
                  </>
                ) : (
                  <>
                    <ShieldCheck size={16} />
                    <span>Grant Admin</span>
                  </>
                )}
              </button>

              <button
                type="button"
                className={`admin-menu-item ${actionsMenuUser.isActive === false ? "admin-menu-item-activate" : "admin-menu-item-deactivate"}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setUserStatusDialog({
                    isOpen: true,
                    user: actionsMenuUser,
                    newStatus: actionsMenuUser.isActive === false,
                  });
                  closeActionsMenu();
                }}
                disabled={
                  deactivatingUsers.has(actionsMenuUser.userId) ||
                  actionsMenuUser.isAnonymous
                }
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  padding: "0.75rem 1rem",
                  background: "none",
                  border: "none",
                  cursor:
                    deactivatingUsers.has(actionsMenuUser.userId) ||
                    actionsMenuUser.isAnonymous
                      ? "not-allowed"
                      : "pointer",
                  opacity: actionsMenuUser.isAnonymous ? 0.5 : 1,
                  color: darkMode ? "#fff" : "#333",
                  fontSize: "0.875rem",
                  textAlign: "left",
                  transition: "background 0.2s",
                }}
                onMouseEnter={(e) => {
                  if (
                    !deactivatingUsers.has(actionsMenuUser.userId) &&
                    !actionsMenuUser.isAnonymous
                  ) {
                    e.currentTarget.style.background = darkMode
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(0, 0, 0, 0.05)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "none";
                }}
              >
                {deactivatingUsers.has(actionsMenuUser.userId) ? (
                  <Loader className="spin" size={16} />
                ) : actionsMenuUser.isActive === false ? (
                  <>
                    <UserCheck size={16} />
                    <span>Activate User</span>
                  </>
                ) : (
                  <>
                    <UserX size={16} />
                    <span>Deactivate User</span>
                  </>
                )}
              </button>

              <button
                type="button"
                className="admin-menu-item"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAdminCancelSubscription(actionsMenuUser);
                  closeActionsMenu();
                }}
                disabled={
                  cancelingSubscriptions.has(actionsMenuUser.userId) ||
                  actionsMenuUser.isAnonymous ||
                  !actionsMenuUser.stripeSubscriptionId ||
                  actionsMenuUser.cancelAtPeriodEnd
                }
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  padding: "0.75rem 1rem",
                  background: "none",
                  border: "none",
                  cursor:
                    cancelingSubscriptions.has(actionsMenuUser.userId) ||
                    actionsMenuUser.isAnonymous ||
                    !actionsMenuUser.stripeSubscriptionId ||
                    actionsMenuUser.cancelAtPeriodEnd
                      ? "not-allowed"
                      : "pointer",
                  opacity:
                    actionsMenuUser.isAnonymous ||
                    !actionsMenuUser.stripeSubscriptionId ||
                    actionsMenuUser.cancelAtPeriodEnd
                      ? 0.5
                      : 1,
                  color: darkMode ? "#fff" : "#333",
                  fontSize: "0.875rem",
                  textAlign: "left",
                  transition: "background 0.2s",
                }}
              >
                {cancelingSubscriptions.has(actionsMenuUser.userId) ? (
                  <>
                    <Loader className="spin" size={16} />
                    <span>Scheduling Cancel...</span>
                  </>
                ) : (
                  <span>
                    {actionsMenuUser.cancelAtPeriodEnd
                      ? "Cancellation Scheduled"
                      : "Cancel Subscription (Admin)"}
                  </span>
                )}
              </button>

              <div
                style={{
                  height: "1px",
                  background: darkMode ? "#444" : "#ddd",
                  margin: "0.5rem 0",
                }}
              />

              <button
                type="button"
                className="admin-menu-item admin-menu-item-delete"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(actionsMenuUser);
                  closeActionsMenu();
                }}
                disabled={deletingUsers.has(actionsMenuUser.userId)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  padding: "0.75rem 1rem",
                  background: "none",
                  border: "none",
                  cursor: deletingUsers.has(actionsMenuUser.userId)
                    ? "not-allowed"
                    : "pointer",
                  color: "#ef4444",
                  fontSize: "0.875rem",
                  textAlign: "left",
                  transition: "background 0.2s",
                }}
                onMouseEnter={(e) => {
                  if (!deletingUsers.has(actionsMenuUser.userId)) {
                    e.currentTarget.style.background =
                      "rgba(239, 68, 68, 0.1)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "none";
                }}
              >
                {deletingUsers.has(actionsMenuUser.userId) ? (
                  <Loader className="spin" size={16} />
                ) : (
                  <>
                    <Trash2 size={16} />
                    <span>Delete User</span>
                  </>
                )}
              </button>
            </div>
          </>,
          document.body,
        )}
      <Footer style={{ marginTop: "4rem" }} />
    </div>
  );
}
